/**
 * Fixed Route UAT Importer
 * 
 * Handles importing stops and routes from UAT into Atlas's fixed-route drawer.
 * 
 * This service:
 * - Extracts unique stops from downloaded UAT operations solutions
 * - Matches stops by UAT stop ID or coordinates (within tolerance)
 * - Creates or updates stops in Atlas with UAT stop IDs preserved
 * - Imports routes with proper stop ID mapping
 */

import type { FixedRouteStop, FixedRoute, FixedRouteStopConfig, FixedRouteSegment } from '../types/fixedRoutes';
import type { UATOperationsSolutionsResponse, UATStop, UATRoute, UATShiftSolution, UATServiceSettingsResponse, UATGeofence, UATHub, UATShiftSolutionsResponse, UATShiftSchedule, UATODBServiceInformation, UATCapacity, UATRidesResponse, UATRideEntry, UATRideDetailedEntry, UATRideSegment } from './fixedRouteUatClient';
import type { Context, Polygon, Point, Line, Layer } from '../context/context';
import type { RideSchema, PassengerType } from '../types/simulation';
import type { Service, Capacity, ProcessingTime, TimeWindowSolution, ServiceFlags } from '../types/service';
import { defaultCapacities } from '../types/service';
import { generateStopsHash } from '../utils/fixedRoutes/routeToLineConverter';
import { v4 as uuid4 } from 'uuid';
import { applyRideAddressPlaceholders } from '../utils/rides/addressPlaceholders';

/**
 * Coordinate matching tolerance in meters
 * Stops within this distance are considered the same location
 */
const COORDINATE_TOLERANCE_METERS = 10;

/**
 * Strip timezone information from ISO datetime string to make it timezone-agnostic
 * Converts formats like "2024-01-15T08:00:00.000Z" or "2024-01-15T08:00:00.000-05:00"
 * to "2024-01-15T08:00:00.000" (timezone-agnostic format)
 * @param datetime - ISO datetime string with optional timezone
 * @returns Timezone-agnostic datetime string in format YYYY-MM-DDTHH:mm:ss.SSS
 */
function stripTimezoneFromDateTime(datetime: string): string {
  if (!datetime || typeof datetime !== 'string') {
    return datetime;
  }
  
  // Remove timezone indicators (Z, +HH:mm, -HH:mm)
  // Match: YYYY-MM-DDTHH:mm:ss.SSS followed by optional timezone
  const timezoneAgnosticPattern = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?)/;
  const match = datetime.match(timezoneAgnosticPattern);
  
  if (match) {
    let result = match[1];
    // Ensure milliseconds are present (3 digits)
    if (!result.includes('.')) {
      result += '.000';
    } else {
      // Pad milliseconds to 3 digits if needed
      const parts = result.split('.');
      if (parts.length === 2) {
        const ms = parts[1].padEnd(3, '0').substring(0, 3);
        result = `${parts[0]}.${ms}`;
      }
    }
    return result;
  }
  
  // If pattern doesn't match, return as-is (might already be timezone-agnostic)
  return datetime;
}

/**
 * Convert UTC datetime to network timezone and return as timezone-agnostic string
 * This ensures that when we store the time, we preserve the local time (not UTC)
 * 
 * @param utcDateTime - UTC datetime string (e.g., "2025-12-11T10:05:00.000Z")
 * @param timezone - Target timezone (e.g., "Australia/Perth", "America/Los_Angeles")
 * @returns Timezone-agnostic datetime string in the network timezone (e.g., "2025-12-11T18:05:00.000")
 */
function convertUTCToNetworkTimezone(utcDateTime: string, timezone: string): string {
  if (!utcDateTime || typeof utcDateTime !== 'string') {
    return utcDateTime;
  }
  
  try {
    // Parse the UTC datetime
    const date = new Date(utcDateTime);
    if (isNaN(date.getTime())) {
      console.warn('[convertUTCToNetworkTimezone] Invalid date:', utcDateTime);
      // Fallback to stripping timezone if parsing fails
      return stripTimezoneFromDateTime(utcDateTime);
    }
    
    // Format the date in the network timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    
    // Get the formatted parts
    const parts = formatter.formatToParts(date);
    const year = parts.find(p => p.type === 'year')?.value || '0000';
    const month = parts.find(p => p.type === 'month')?.value || '01';
    const day = parts.find(p => p.type === 'day')?.value || '01';
    const hour = parts.find(p => p.type === 'hour')?.value || '00';
    const minute = parts.find(p => p.type === 'minute')?.value || '00';
    const second = parts.find(p => p.type === 'second')?.value || '00';
    
    // Extract milliseconds from original string if present
    const msMatch = utcDateTime.match(/\.(\d{1,3})/);
    const milliseconds = msMatch ? msMatch[1].padEnd(3, '0').substring(0, 3) : '000';
    
    // Return as timezone-agnostic string
    return `${year}-${month}-${day}T${hour}:${minute}:${second}.${milliseconds}`;
  } catch (error) {
    console.warn('[convertUTCToNetworkTimezone] Error converting timezone, falling back to strip:', error);
    // Fallback to stripping timezone if conversion fails
    return stripTimezoneFromDateTime(utcDateTime);
  }
}

/**
 * Parse time from various string formats (ISO datetime, time string, etc.)
 * @param timeStr - Time string to parse (ISO datetime or HH:mm format)
 * @param timezone - Optional timezone (e.g., 'America/New_York') for parsing ISO datetime strings.
 *                   If not provided, uses the user's local timezone (not recommended for timezone-agnostic design).
 */
function parseTimeFromString(timeStr: string, timezone?: string): { hours: number; minutes: number } | null {
  if (!timeStr) return null;
  
  try {
    // Try parsing as ISO datetime string
    const date = new Date(timeStr);
    if (!isNaN(date.getTime())) {
      // If timezone is provided, extract hours/minutes in that timezone
      if (timezone) {
        try {
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          });
          const parts = formatter.formatToParts(date);
          const hours = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
          const minutes = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
          return { hours, minutes };
        } catch (tzError) {
          console.warn('[parseTimeFromString] Failed to format in timezone, falling back to local:', timezone, tzError);
          // Fall through to local timezone extraction
        }
      }
      // Fallback to local timezone (for backwards compatibility, but not recommended)
      return {
        hours: date.getHours(),
        minutes: date.getMinutes(),
      };
    }
    
    // Try parsing as time string (HH:mm or HH:mm:ss)
    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (timeMatch) {
      return {
        hours: parseInt(timeMatch[1], 10),
        minutes: parseInt(timeMatch[2], 10),
      };
    }
  } catch (e) {
    console.warn('[parseTimeFromString] Failed to parse time:', timeStr, e);
  }
  
  return null;
}

/**
 * Convert meters to approximate degrees (rough approximation for small distances)
 * 1 degree latitude ≈ 111,000 meters
 */
function metersToDegrees(meters: number): number {
  return meters / 111000;
}

/**
 * Calculate distance between two coordinates in meters (Haversine formula)
 */
function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Split a route display name into its base name and an optional trailing
 * "Version N" suffix. UAT route labels sometimes include the version in the
 * same string, but Atlas route names should stay version-free so upload
 * matching keeps working.
 */
function splitRouteVersionSuffix(name: string): { baseName: string; versionSuffix?: string } {
  const trimmed = (name || '').trim();
  const match = trimmed.match(/^(.*?)(?:\s+Version\s+(\d+))$/i);
  if (match && match[1] && match[1].trim()) {
    return {
      baseName: match[1].trim(),
      versionSuffix: `Version ${match[2]}`,
    };
  }
  return { baseName: trimmed };
}

/**
 * Extract unique stops from UAT operations solutions
 * 
 * @param response - UAT operations solutions response
 * @returns Map of UAT stop ID to stop data
 */
export function extractUATStops(
  response: UATOperationsSolutionsResponse
): Map<string, UATStop> {
  const stopMap = new Map<string, UATStop>();

  if (!response.data?.entries) {
    console.warn('[extractUATStops] No entries in response');
    return stopMap;
  }

  // Log only if debugging is needed
  // console.log('[extractUATStops] Processing', response.data.entries.length, 'entries');

  // Iterate through all operations solutions
  for (const entry of response.data.entries) {
    if (!entry.shiftSolutions) continue;

    // Iterate through all shift solutions
    for (const shiftSolution of entry.shiftSolutions) {
      if (!shiftSolution.route) continue;

      const route = shiftSolution.route;

      // Extract stops from route.stops[] (preferred source)
      if (route.stops) {
        for (const stop of route.stops) {
          if (stop.id && !stopMap.has(stop.id)) {
            const stopAny = stop as any; // Cast to access label, latitude, longitude, etc.
            
            // Try multiple coordinate formats
            let coordinates = stop.coordinates;
            if (!coordinates || coordinates.lat === undefined || coordinates.lng === undefined) {
              // Try alternative formats
              if (stopAny.coords && (stopAny.coords.lat !== undefined || stopAny.coords.lng !== undefined)) {
                coordinates = stopAny.coords;
              } else if (stopAny.lat !== undefined && stopAny.lng !== undefined) {
                coordinates = { lat: stopAny.lat, lng: stopAny.lng };
              } else if (stopAny.latitude !== undefined && stopAny.longitude !== undefined) {
                coordinates = { lat: stopAny.latitude, lng: stopAny.longitude };
              } else if (stopAny.location) {
                // Try nested location object
                const loc = stopAny.location;
                if (loc.lat !== undefined && loc.lng !== undefined) {
                  coordinates = { lat: loc.lat, lng: loc.lng };
                } else if (loc.latitude !== undefined && loc.longitude !== undefined) {
                  coordinates = { lat: loc.latitude, lng: loc.longitude };
                } else if (loc.coordinates && Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
                  // GeoJSON format [lng, lat]
                  coordinates = { lat: loc.coordinates[1], lng: loc.coordinates[0] };
                }
              }
            }
            
            // Extract name - prefer "label" over "name" (UAT format)
            const stopName = stopAny.label || stop.name || stopAny.description;
            
            const stopWithCoords: UATStop = {
              ...stop,
              coordinates: coordinates,
              // Use label as name if available (UAT format)
              name: stopName,
              // Preserve address and description from UAT
              address: stopAny.address,
              description: stopAny.description,
            };
            
            // Log only if debugging is needed
            // if (stopMap.size < 3) {
            //   console.log('[extractUATStops] Extracted stop from route.stops:', {
            //     id: stop.id,
            //     label: stopAny.label,
            //     name: stop.name,
            //     extractedName: stopName,
            //     hasCoordinates: !!stopWithCoords.coordinates,
            //     coordinates: stopWithCoords.coordinates,
            //     stopKeys: Object.keys(stop),
            //     fullStop: stop
            //   });
            // }
            
            stopMap.set(stop.id, stopWithCoords);
          }
        }
      }
      
      // Also extract stops from routeStops.next[] to get names and timing
      // This is important because routeStops.next often has better data (names, expectedArrivalTime)
      if (route.routeStops?.next) {
        for (const routeStop of route.routeStops.next) {
          if (routeStop.id) {
            const routeStopAny = routeStop as any;
            
            // Extract name - prefer "label" over "name" (UAT format)
            const stopName = routeStopAny.label || routeStop.name || routeStopAny.description;
            
            // If stop already exists, update it with name if missing
            const existingStop = stopMap.get(routeStop.id);
            if (existingStop) {
              // Update name if routeStops.next has a better name (label or name)
              if (stopName && (!existingStop.name || existingStop.name.trim() === '' || existingStop.name.startsWith('Stop ') || existingStop.name.match(/^Stop [0-9a-f-]{36}$/i))) {
                existingStop.name = stopName;
                // Log only if debugging is needed
                // console.log(`[extractUATStops] Updated stop ${routeStop.id} name from routeStops.next: ${stopName}`);
              }
              // Update coordinates if missing - try latitude/longitude format
              if ((!existingStop.coordinates || existingStop.coordinates.lat === 0) && routeStop.coordinates) {
                existingStop.coordinates = routeStop.coordinates;
              } else if ((!existingStop.coordinates || existingStop.coordinates.lat === 0) && routeStopAny.latitude !== undefined && routeStopAny.longitude !== undefined) {
                existingStop.coordinates = { lat: routeStopAny.latitude, lng: routeStopAny.longitude };
              }
            } else {
              // New stop from routeStops.next
              const routeStopAny = routeStop as any; // Cast to access latitude, longitude, address, description, etc.
              
              let coordinates = routeStop.coordinates;
              if (!coordinates || coordinates.lat === undefined || coordinates.lng === undefined) {
                // Try latitude/longitude directly (common in UAT responses)
                if (routeStopAny.latitude !== undefined && routeStopAny.longitude !== undefined) {
                  coordinates = { lat: routeStopAny.latitude, lng: routeStopAny.longitude };
                } else if (routeStopAny.coords && (routeStopAny.coords.lat !== undefined || routeStopAny.coords.lng !== undefined)) {
                  coordinates = routeStopAny.coords;
                } else if (routeStopAny.lat !== undefined && routeStopAny.lng !== undefined) {
                  coordinates = { lat: routeStopAny.lat, lng: routeStopAny.lng };
                } else if (routeStopAny.location) {
                  const loc = routeStopAny.location;
                  if (loc.lat !== undefined && loc.lng !== undefined) {
                    coordinates = { lat: loc.lat, lng: loc.lng };
                  } else if (loc.latitude !== undefined && loc.longitude !== undefined) {
                    coordinates = { lat: loc.latitude, lng: loc.longitude };
                  } else if (loc.coordinates && Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
                    coordinates = { lat: loc.coordinates[1], lng: loc.coordinates[0] };
                  }
                }
              }
              
              // Extract stop with name from routeStops.next - use label as name
              const stopWithName: UATStop = {
                id: routeStop.id,
                name: stopName, // Use label as name (e.g., "T4", "Parking Lot", etc.)
                coordinates: coordinates,
                order: routeStop.order,
                // Preserve address and description from UAT
                address: routeStopAny.address,
                description: routeStopAny.description,
              };
              
              // Log first few stops for debugging
              if (stopMap.size < 3) {
                console.log('[extractUATStops] Extracted stop from routeStops.next:', {
                  id: routeStop.id,
                  label: routeStopAny.label,
                  name: routeStop.name,
                  extractedName: stopName,
                  hasCoordinates: !!stopWithName.coordinates,
                  coordinates: stopWithName.coordinates,
                  routeStopKeys: Object.keys(routeStop),
                  fullRouteStop: routeStop
                });
              }
              
              stopMap.set(routeStop.id, stopWithName);
            }
          }
        }
      }

      // Extract stops from routeStops.next[] - this often has better data (names, expectedArrivalTime)
      // Always process this to update existing stops with names if missing
      if (route.routeStops?.next) {
        for (const routeStop of route.routeStops.next) {
          if (routeStop.id) {
            const existingStop = stopMap.get(routeStop.id);
            
            if (existingStop) {
              // Update existing stop with name from routeStops.next if missing or generic
              if (routeStop.name && 
                  (!existingStop.name || 
                   existingStop.name.trim() === '' || 
                   existingStop.name.startsWith('Stop ') ||
                   existingStop.name.match(/^Stop [0-9a-f-]{36}$/i))) {
                existingStop.name = routeStop.name;
                // Log only if debugging is needed
                // console.log(`[extractUATStops] Updated stop ${routeStop.id} name: ${routeStop.name}`);
              }
              // Update coordinates if missing
              if ((!existingStop.coordinates || existingStop.coordinates.lat === 0) && routeStop.coordinates) {
                existingStop.coordinates = routeStop.coordinates;
              }
            } else {
              // New stop from routeStops.next
              let coordinates = routeStop.coordinates;
              if (!coordinates || coordinates.lat === undefined || coordinates.lng === undefined) {
                const routeStopAny = routeStop as any;
                // Try alternative formats
                if (routeStopAny.coords && (routeStopAny.coords.lat !== undefined || routeStopAny.coords.lng !== undefined)) {
                  coordinates = routeStopAny.coords;
                } else if (routeStopAny.lat !== undefined && routeStopAny.lng !== undefined) {
                  coordinates = { lat: routeStopAny.lat, lng: routeStopAny.lng };
                } else if (routeStopAny.latitude !== undefined && routeStopAny.longitude !== undefined) {
                  coordinates = { lat: routeStopAny.latitude, lng: routeStopAny.longitude };
                } else if (routeStopAny.location) {
                  const loc = routeStopAny.location;
                  if (loc.lat !== undefined && loc.lng !== undefined) {
                    coordinates = { lat: loc.lat, lng: loc.lng };
                  } else if (loc.latitude !== undefined && loc.longitude !== undefined) {
                    coordinates = { lat: loc.latitude, lng: loc.longitude };
                  } else if (loc.coordinates && Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
                    coordinates = { lat: loc.coordinates[1], lng: loc.coordinates[0] };
                  }
                }
              }
              
              // Extract stop with name from routeStops.next
              const routeStopAny2 = routeStop as any;
              const stopWithName: UATStop = {
                id: routeStop.id,
                name: routeStop.name, // This should have the proper name like "Parking Lot", "T2", etc.
                coordinates: coordinates,
                order: routeStop.order,
                // Preserve address and description from UAT
                address: routeStopAny2.address,
                description: routeStopAny2.description,
              };
              
              // Log first few stops for debugging
              if (stopMap.size < 3) {
                console.log('[extractUATStops] Extracted stop from routeStops.next:', {
                  id: routeStop.id,
                  name: routeStop.name,
                  hasCoordinates: !!stopWithName.coordinates,
                  coordinates: stopWithName.coordinates,
                  routeStopKeys: Object.keys(routeStop),
                  fullRouteStop: routeStop
                });
              }
              
              stopMap.set(routeStop.id, stopWithName);
            }
          }
        }
      }
    }
  }

  // Log only if debugging is needed
  // console.log('[extractUATStops] Extracted', stopMap.size, 'unique stops');
  // let withCoords = 0;
  // for (const stop of Array.from(stopMap.values())) {
  //   if (stop.coordinates && stop.coordinates.lat !== undefined && stop.coordinates.lng !== undefined) {
  //     withCoords++;
  //   }
  // }
  // console.log('[extractUATStops] Stops with coordinates:', withCoords, 'of', stopMap.size);

  return stopMap;
}

/**
 * Import result for a single stop
 */
export interface StopImportResult {
  stop: FixedRouteStop;
  action: 'created' | 'updated' | 'skipped';
  reason?: string;
}

/**
 * Import stops from UAT into Atlas
 * 
 * @param uatStops - Map of UAT stop ID to stop data
 * @param existingStops - Existing stops in Atlas
 * @returns Array of import results
 */
export function importUATStops(
  uatStops: Map<string, UATStop>,
  existingStops: FixedRouteStop[]
): StopImportResult[] {
  const results: StopImportResult[] = [];
  const existingByUatId = new Map<string, FixedRouteStop>();
  const existingByCoords = new Map<string, FixedRouteStop[]>();

  // Build lookup maps
  for (const stop of existingStops) {
    // Index by UAT stop ID if present
    if (stop.uatStopId) {
      existingByUatId.set(stop.uatStopId, stop);
    }

    // Index by coordinates (rounded to avoid floating point issues)
    const coordKey = `${Math.round(stop.coordinates.lat * 1000) / 1000},${Math.round(stop.coordinates.lng * 1000) / 1000}`;
    if (!existingByCoords.has(coordKey)) {
      existingByCoords.set(coordKey, []);
    }
    existingByCoords.get(coordKey)!.push(stop);
  }

  // Process each UAT stop
  for (const [uatStopId, uatStop] of Array.from(uatStops.entries())) {
    // Check for coordinates in various possible formats
    let coordinates = uatStop.coordinates;
    if (!coordinates || coordinates.lat === undefined || coordinates.lng === undefined) {
      // Try alternative coordinate formats
      const stopAny = uatStop as any;
      if (stopAny.coords && (stopAny.coords.lat !== undefined || stopAny.coords.lng !== undefined)) {
        coordinates = stopAny.coords;
      } else if (stopAny.lat !== undefined && stopAny.lng !== undefined) {
        coordinates = { lat: stopAny.lat, lng: stopAny.lng };
      } else if (stopAny.latitude !== undefined && stopAny.longitude !== undefined) {
        coordinates = { lat: stopAny.latitude, lng: stopAny.longitude };
      }
    }
    
    // Validate coordinates are valid numbers (not just defined)
    const hasValidCoordinates = coordinates && 
      typeof coordinates.lat === 'number' && 
      typeof coordinates.lng === 'number' &&
      !isNaN(coordinates.lat) && 
      !isNaN(coordinates.lng) &&
      isFinite(coordinates.lat) &&
      isFinite(coordinates.lng);
    
    // If coordinates are missing or invalid, skip this stop
    if (!hasValidCoordinates || !coordinates) {
      console.warn(`[importUATStops] Stop ${uatStopId} missing or invalid coordinates`, {
        stop: uatStop,
        hasCoordinates: !!uatStop.coordinates,
        coordinates: uatStop.coordinates,
        extractedCoordinates: coordinates,
        stopKeys: Object.keys(uatStop),
        existingStopsCount: existingStops.length
      });
      
      // Skip stops with invalid coordinates - they cannot be displayed on the map
      results.push({
        stop: {
          id: uuid4(),
          name: uatStop.name || `Stop ${uatStopId}`,
          coordinates: { lat: 0, lng: 0 }, // Placeholder for skipped stops
          uatStopId,
          address: uatStop.address,
          description: uatStop.description,
        },
        action: 'skipped',
        reason: 'Missing or invalid coordinates',
      });
      continue;
    }
    
    // At this point, TypeScript knows coordinates is defined and valid
    // Create a validated coordinates object with proper typing
    const validCoordinates: { lat: number; lng: number } = {
      lat: coordinates.lat,
      lng: coordinates.lng,
    };
    
    // Log only if debugging is needed
    // console.log(`[importUATStops] Processing stop ${uatStopId}:`, {
    //   name: uatStop.name,
    //   hasCoordinates: true,
    //   coordinates: validCoordinates
    // });

    let matchedStop: FixedRouteStop | null = null;
    let action: 'created' | 'updated' | 'skipped' = 'created';

    // First, try to match by UAT stop ID
    matchedStop = existingByUatId.get(uatStopId) || null;

    // If not found, try to match by coordinates
    if (!matchedStop) {
      const coordKey = `${Math.round(validCoordinates.lat * 1000) / 1000},${Math.round(validCoordinates.lng * 1000) / 1000}`;
      const candidates = existingByCoords.get(coordKey) || [];

      // Check if any candidate is within tolerance
      for (const candidate of candidates) {
        const distance = calculateDistance(
          validCoordinates.lat,
          validCoordinates.lng,
          candidate.coordinates.lat,
          candidate.coordinates.lng
        );

        if (distance <= COORDINATE_TOLERANCE_METERS) {
          matchedStop = candidate;
          action = 'updated';
          break;
        }
      }
    } else {
      action = 'updated';
    }

    // Create or update stop
    if (matchedStop) {
      // Update existing stop with UAT stop ID if not already set
      // Preserve stop name from UAT if available and current stop name is generic
      const shouldUpdateName = uatStop.name && 
        uatStop.name.trim() !== '' && 
        (matchedStop.name === `Stop ${matchedStop.id}` || 
         matchedStop.name === `Stop ${matchedStop.uatStopId || ''}` ||
         !matchedStop.name ||
         matchedStop.name.match(/^Stop [0-9a-f-]{36}$/i)); // UUID pattern
      
      // Ensure name is always a string
      const finalName = shouldUpdateName && uatStop.name
        ? uatStop.name.trim()
        : (matchedStop.name || uatStop.name || `Stop ${uatStopId}`);
      
      // Ensure matched stop has valid coordinates before updating
      const existingCoords = matchedStop.coordinates;
      const hasValidExistingCoords = existingCoords &&
        typeof existingCoords.lat === 'number' &&
        typeof existingCoords.lng === 'number' &&
        !isNaN(existingCoords.lat) &&
        !isNaN(existingCoords.lng) &&
        isFinite(existingCoords.lat) &&
        isFinite(existingCoords.lng);
      
      // UAT coordinates are already validated above (validCoordinates)
      // Use UAT coordinates if they're significantly different from existing (UAT is source of truth)
      let finalCoordinates: { lat: number; lng: number } = validCoordinates; // Default to UAT coordinates (already validated)
      if (hasValidExistingCoords) {
        // Both are valid - use UAT if significantly different
        const distance = calculateDistance(
          validCoordinates.lat,
          validCoordinates.lng,
          matchedStop.coordinates.lat,
          matchedStop.coordinates.lng
        );
        // Use UAT coordinates if they're more than tolerance away (UAT is source of truth)
        if (distance <= COORDINATE_TOLERANCE_METERS) {
          // Keep existing if within tolerance (preserves user edits)
          finalCoordinates = matchedStop.coordinates;
        }
        // Otherwise use UAT coordinates (already set above)
      }
      // If existing coordinates are invalid, use UAT coordinates (already set above)
      
      const updatedStop: FixedRouteStop = {
        ...matchedStop,
        uatStopId: matchedStop.uatStopId || uatStopId,
        // Update name if UAT has a better name
        name: finalName,
        // Update address and description if available from UAT
        address: uatStop.address || matchedStop.address,
        description: uatStop.description || matchedStop.description,
        // Preserve label if available from UAT
        label: (uatStop as any).label || matchedStop.label,
        // Preserve placeId if available from UAT
        placeId: (uatStop as any).placeId || matchedStop.placeId,
        // Use validated coordinates
        coordinates: finalCoordinates,
        // Preserve or update UAT metadata
        uatMetadata: {
          originalId: uatStopId,
          originalName: uatStop.name,
          originalLabel: (uatStop as any).label,
          importedAt: Date.now(),
          ...matchedStop.uatMetadata, // Preserve existing metadata if present
        },
      };
      results.push({ stop: updatedStop, action });
    } else {
      // Create new stop - ALWAYS create if we have coordinates, even if no match found
      // Use stop name from UAT, or create a more user-friendly name
      let stopName = uatStop.name;
      if (!stopName || stopName.trim() === '') {
        // Try to create a better name from the stop ID or use a generic name
        // If the ID is a UUID, use a shorter generic name
        if (uatStopId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          stopName = `Stop ${uatStopId.substring(0, 8)}`;
        } else {
          stopName = `Stop ${uatStopId}`;
        }
      } else {
        // Clean up the name - remove extra whitespace
        stopName = stopName.trim();
      }
      
      // Coordinates are already validated above (validCoordinates)
      // If we reach here, coordinates are guaranteed to be valid numbers
      const newStop: FixedRouteStop = {
        id: uuid4(),
        name: stopName,
        coordinates: validCoordinates, // Already validated as valid above
        uatStopId,
        address: uatStop.address,
        description: uatStop.description,
        // Preserve label if available
        label: (uatStop as any).label,
        // Preserve placeId if available
        placeId: (uatStop as any).placeId,
        // Store UAT metadata for future reference
        uatMetadata: {
          originalId: uatStopId,
          originalName: uatStop.name,
          originalLabel: (uatStop as any).label,
          importedAt: Date.now(),
        },
      };
      results.push({ stop: newStop, action: 'created' });
    }
  }

  return results;
}

/**
 * Extract routes from UAT operations solutions
 * 
 * @param response - UAT operations solutions response
 * @param timezone - Optional timezone (e.g., 'America/New_York') for parsing datetime strings.
 *                   If provided, times will be extracted in this timezone instead of user's local timezone.
 * @returns Array of route data with their associated shift solutions
 */
export function extractUATRoutes(
  response: UATOperationsSolutionsResponse,
  timezone?: string
): Array<{
  route: UATRoute;
  routeId: string;
  routeName: string;
  routeColor: string;
  routeShape: [number, number][];
  stops: Array<{
    uatStopId: string;
    order: number;
    timeToNextStop: number;
    timeSpentAtStop: number;
  }>;
}> {
  const routes: Array<{
    route: UATRoute;
    routeId: string;
    routeName: string;
    routeColor: string;
    routeShape: [number, number][];
    stops: Array<{
      uatStopId: string;
      order: number;
      timeToNextStop: number;
      timeSpentAtStop: number;
    }>;
  }> = [];

  if (!response.data?.entries) {
    return routes;
  }

  // Track unique routes by route ID
  const routeMap = new Map<string, {
    route: UATRoute;
    routeId: string;
    routeName: string;
    routeColor: string;
    routeShape: [number, number][];
    stops: Map<number, {
      uatStopId: string;
      order: number;
      timeToNextStop: number;
      timeSpentAtStop: number;
    }>;
  }>();

  // Iterate through all operations solutions
  for (const entry of response.data.entries) {
    if (!entry.shiftSolutions) continue;

    // Iterate through all shift solutions
    for (const shiftSolution of entry.shiftSolutions) {
      if (!shiftSolution.route) continue;

      const route = shiftSolution.route;
      const routeId = route.id;

      if (!routeMap.has(routeId)) {
        // Extract route shape - validate format
        const routeShape: [number, number][] = [];
        if (route.routeShape?.orderedCoordinates) {
          const coords = route.routeShape.orderedCoordinates;
          // Handle different coordinate formats
          if (Array.isArray(coords)) {
            for (const coord of coords) {
              if (Array.isArray(coord) && coord.length >= 2) {
                // Format: [[lat, lng], [lat, lng], ...]
                const lat = coord[0];
                const lng = coord[1];
                if (typeof lat === 'number' && typeof lng === 'number' && 
                    !isNaN(lat) && !isNaN(lng) && 
                    isFinite(lat) && isFinite(lng)) {
                  routeShape.push([lat, lng]);
                }
              } else if (coord && typeof coord === 'object') {
                // Try different object formats
                let lat: number | undefined;
                let lng: number | undefined;
                
                // Format: {latitude: number, longitude: number} (UAT format)
                if ('latitude' in coord && 'longitude' in coord) {
                  lat = (coord as any).latitude;
                  lng = (coord as any).longitude;
                }
                // Format: {lat: number, lng: number}
                else if ('lat' in coord && 'lng' in coord) {
                  lat = (coord as any).lat;
                  lng = (coord as any).lng;
                }
                // Format: {lat: number, lon: number}
                else if ('lat' in coord && 'lon' in coord) {
                  lat = (coord as any).lat;
                  lng = (coord as any).lon;
                }
                
                if (lat !== undefined && lng !== undefined &&
                    typeof lat === 'number' && typeof lng === 'number' && 
                    !isNaN(lat) && !isNaN(lng) && 
                    isFinite(lat) && isFinite(lng)) {
                  routeShape.push([lat, lng]);
                }
              }
            }
          }
        }

        routeMap.set(routeId, {
          route,
          routeId,
          routeName: route.label || `Route ${routeId}`,
          routeColor: route.colour || '#000000',
          routeShape,
          stops: new Map(),
        });
      }

      const routeData = routeMap.get(routeId)!;

      // Extract stops from route.stops[] (preferred source for basic structure)
      if (route.stops && route.stops.length > 0) {
        for (let i = 0; i < route.stops.length; i++) {
          const stop = route.stops[i];
          if (stop.id) {
            const nextStop = route.stops[i + 1];
            routeData.stops.set(stop.order ?? i, {
              uatStopId: stop.id,
              order: stop.order ?? i,
              timeToNextStop: stop.timeToNextStop ?? 0,
              timeSpentAtStop: stop.timeSpentAtStop ?? 0,
            });
          }
        }
      }
      
      // Always check routeStops.next[] for timing data (expectedArrivalTime)
      // This often has more accurate timing information than route.stops[]
      if (route.routeStops?.next) {
        for (let i = 0; i < route.routeStops.next.length; i++) {
          const routeStop = route.routeStops.next[i];
          if (routeStop.id) {
            const nextRouteStop = route.routeStops.next[i + 1];
            
            // Calculate timeToNextStop and timeSpentAtStop from expectedArrivalTime if available
            let timeToNextStop = 0;
            let timeSpentAtStop = 0;
            
            if (routeStop.expectedArrivalTime) {
              const currentTime = parseTimeFromString(routeStop.expectedArrivalTime, timezone);
              
              if (currentTime && nextRouteStop?.expectedArrivalTime) {
                // Calculate timing from expectedArrivalTime differences
                const nextTime = parseTimeFromString(nextRouteStop.expectedArrivalTime, timezone);
                if (nextTime) {
                  const currentMinutes = currentTime.hours * 60 + currentTime.minutes;
                  const nextMinutes = nextTime.hours * 60 + nextTime.minutes;
                  const totalMinutes = nextMinutes > currentMinutes 
                    ? nextMinutes - currentMinutes 
                    : (nextMinutes + 24 * 60) - currentMinutes; // Handle overnight
                  
                  // Estimate wait time: typically 1-2 minutes at stops
                  // For short intervals (< 5 min), assume 1 min wait, otherwise 2 min
                  timeSpentAtStop = totalMinutes < 5 ? 1 : 2;
                  timeToNextStop = Math.max(0, totalMinutes - timeSpentAtStop);
                  
                  // Log only if debugging is needed
                  // if (i < 3) {
                  //   console.log(`[extractUATRoutes] Calculated timing for stop ${routeStop.id} (${routeStop.name || 'unnamed'}):`, {
                  //     currentTime: routeStop.expectedArrivalTime,
                  //     nextTime: nextRouteStop.expectedArrivalTime,
                  //     totalMinutes,
                  //     timeToNextStop,
                  //     timeSpentAtStop
                  //   });
                  // }
                }
              } else {
                // Last stop or no next stop - default wait time
                timeSpentAtStop = 1;
              }
            }
            
            // Update or set the stop timing data
            const existingStopData = routeData.stops.get(routeStop.order ?? i);
            if (existingStopData) {
              // Update existing timing if we calculated better values from expectedArrivalTime
              if (timeToNextStop > 0 || timeSpentAtStop > 0) {
                existingStopData.timeToNextStop = timeToNextStop;
                existingStopData.timeSpentAtStop = timeSpentAtStop;
              }
            } else {
              // Create new stop data entry (if route.stops[] didn't have this stop)
              routeData.stops.set(routeStop.order ?? i, {
                uatStopId: routeStop.id,
                order: routeStop.order ?? i,
                timeToNextStop,
                timeSpentAtStop,
              });
            }
          }
        }
      }
    }
  }

  // Convert to array format
  for (const routeData of Array.from(routeMap.values())) {
    const stopValues = Array.from(routeData.stops.values());
    routes.push({
      route: routeData.route,
      routeId: routeData.routeId,
      routeName: routeData.routeName,
      routeColor: routeData.routeColor,
      routeShape: routeData.routeShape,
      stops: stopValues.sort((a, b) => a.order - b.order),
    });
  }

  return routes;
}

/**
 * Extract shifts from UAT operations solutions
 * 
 * @param response - UAT operations solutions response
 * @returns Array of shift data
 */
export function extractUATShifts(
  response: UATOperationsSolutionsResponse
): UATShiftSolution[] {
  const shifts: UATShiftSolution[] = [];

  if (!response.data?.entries) {
    console.warn('[extractUATShifts] No entries in response');
    return shifts;
  }

  console.log('[extractUATShifts] Processing', response.data.entries.length, 'entries');

  // Iterate through all operations solutions
  for (const entry of response.data.entries) {
    if (!entry.shiftSolutions) continue;

    // Add all shift solutions
    for (const shiftSolution of entry.shiftSolutions) {
      shifts.push(shiftSolution);
    }
  }

  console.log('[extractUATShifts] Extracted', shifts.length, 'shifts');
  return shifts;
}

/**
 * Extract unique vehicles/fleets from UAT operations solutions
 * 
 * @param response - UAT operations solutions response
 * @returns Map of vehicle ID to vehicle data
 */
export function extractUATFleets(
  response: UATOperationsSolutionsResponse
): Map<string, {
  id: string;
  name?: string;
  picture?: string;
  capacities?: any[];
  shifts: UATShiftSolution[];
}> {
  const vehicleMap = new Map<string, {
    id: string;
    name?: string;
    picture?: string;
    capacities?: any[];
    shifts: UATShiftSolution[];
  }>();

  if (!response.data?.entries) {
    console.warn('[extractUATFleets] No entries in response');
    return vehicleMap;
  }

  console.log('[extractUATFleets] Processing', response.data.entries.length, 'entries');

  // Iterate through all operations solutions
  for (const entry of response.data.entries) {
    if (!entry.shiftSolutions) continue;

    // Iterate through all shift solutions
    for (const shiftSolution of entry.shiftSolutions) {
      if (!shiftSolution.vehicle?.id) continue;

      const vehicleId = shiftSolution.vehicle.id;
      
      if (!vehicleMap.has(vehicleId)) {
        vehicleMap.set(vehicleId, {
          id: vehicleId,
          name: shiftSolution.vehicle.name,
          picture: shiftSolution.vehicle.picture,
          capacities: shiftSolution.vehicle.capacities,
          shifts: [],
        });
      }

      const vehicle = vehicleMap.get(vehicleId)!;
      vehicle.shifts.push(shiftSolution);
    }
  }

  console.log('[extractUATFleets] Extracted', vehicleMap.size, 'unique vehicles');
  return vehicleMap;
}

/**
 * Extract ODB fleets from UAT shift solutions response
 * 
 * The ODB fleet API returns a different structure than FR fleets:
 * - Response has `data.solutions[]` (not `data.entries[]`)
 * - Each solution has `schedules[]` (not `shiftSolutions[]`)
 * - Each schedule has a `vehicle` object with `id`, `label`, and `seatingCapacity[]`
 * - Each schedule has `startTime` and `endTime` for the shift
 * 
 * @param response - UAT shift solutions response
 * @returns Map of vehicle ID to fleet data
 */
export function extractUATODBFleets(
  response: UATShiftSolutionsResponse
): Map<string, {
  id: string;
  name?: string;
  picture?: string;
  capacities?: any[];
  shifts: Array<{
    id: string;
    startDate: string;
    endDate: string;
    scheduleId: string;
    solutionId: string;
    solutionName: string;
    schedule?: UATShiftSchedule; // Preserve full schedule data for location extraction
  }>;
}> {
  const vehicleMap = new Map<string, {
    id: string;
    name?: string;
    picture?: string;
    capacities?: any[];
    shifts: Array<{
      id: string;
      startDate: string;
      endDate: string;
      scheduleId: string;
      solutionId: string;
      solutionName: string;
      schedule?: UATShiftSchedule; // Preserve full schedule data for location extraction
    }>;
  }>();

  if (!response.data?.solutions) {
    console.warn('[extractUATODBFleets] No solutions in response');
    return vehicleMap;
  }

  console.log('[extractUATODBFleets] Processing', response.data.solutions.length, 'solutions');

  // Iterate through all solutions
  for (const solution of response.data.solutions) {
    if (!solution.schedules || !Array.isArray(solution.schedules)) {
      console.log('[extractUATODBFleets] Solution', solution.solutionId, 'has no schedules or schedules is not an array');
      continue;
    }

    console.log('[extractUATODBFleets] Solution', solution.solutionId, 'has', solution.schedules.length, 'schedules');

    // Iterate through all schedules in this solution
    for (const schedule of solution.schedules) {
      if (!schedule.vehicle?.id) {
        console.log('[extractUATODBFleets] Schedule', schedule.scheduleId, 'has no vehicle or vehicle.id');
        continue;
      }

      const vehicleId = schedule.vehicle.id;
      
      if (!vehicleMap.has(vehicleId)) {
        // Convert seatingCapacity to capacities format
        const capacities = schedule.vehicle.seatingCapacity.map(cap => ({
          type: cap.capacityName.toLowerCase(),
          count: cap.amount,
        }));

        console.log('[extractUATODBFleets] Adding new vehicle:', {
          id: vehicleId,
          label: schedule.vehicle.label,
          capacities,
        });

        vehicleMap.set(vehicleId, {
          id: vehicleId,
          name: schedule.vehicle.label || `Vehicle ${vehicleId.substring(0, 8)}`,
          picture: undefined, // ODB API doesn't provide pictures
          capacities,
          shifts: [],
        });
      }

      const vehicle = vehicleMap.get(vehicleId)!;
      
      // Convert schedule to shift format - preserve full schedule data for location extraction
      vehicle.shifts.push({
        id: schedule.scheduleId,
        startDate: schedule.startTime,
        endDate: schedule.endTime,
        scheduleId: schedule.scheduleId,
        solutionId: solution.solutionId,
        solutionName: solution.name,
        // Preserve full schedule data for location extraction
        schedule: schedule, // Include full schedule with stops, activities, paths, etc.
      });
    }
  }

  console.log('[extractUATODBFleets] Extracted', vehicleMap.size, 'unique ODB vehicles');
  if (vehicleMap.size > 0) {
    const firstVehicle = Array.from(vehicleMap.values())[0];
    console.log('[extractUATODBFleets] Sample vehicle:', {
      id: firstVehicle.id,
      name: firstVehicle.name,
      shiftsCount: firstVehicle.shifts.length,
      capacities: firstVehicle.capacities,
    });
  }
  return vehicleMap;
}

/**
 * Import ODB fleets from UAT into Atlas
 * 
 * @param fleets - Map of fleet data from UAT
 * @param existingFleets - Existing fleets in Atlas
 * @param serviceType - Service type ('on-demand' for ODB fleets)
 * @returns Array of import results
 */
export function importUATODBFleets(
  fleets: Map<string, {
    id: string;
    name?: string;
    picture?: string;
    capacities?: any[];
    shifts: UATShiftSolution[];
  }>,
  existingFleets: any[],
  serviceType: 'on-demand' = 'on-demand'
): Array<{ fleetId: string; action: 'created' | 'skipped'; reason?: string }> {
  // This function will be called from FleetsDownloadSection
  // The actual import logic is in the component's handleImportODB function
  // This is a placeholder that can be expanded if needed
  const results: Array<{ fleetId: string; action: 'created' | 'skipped'; reason?: string }> = [];
  
  // Convert Map entries to array to avoid downlevelIteration issues
  const fleetEntries = Array.from(fleets.entries());
  for (const [fleetId, fleet] of fleetEntries) {
    results.push({
      fleetId,
      action: 'created', // Actual import happens in component
    });
  }
  
  return results;
}

/**
 * Import result for a single route
 */
export interface RouteImportResult {
  route: FixedRoute;
  action: 'created' | 'skipped';
  reason?: string;
  missingStops: string[]; // UAT stop IDs that weren't found in Atlas
}

/**
 * Import routes from UAT into Atlas
 * 
 * @param uatRoutes - Array of UAT route data
 * @param existingStops - Existing stops in Atlas (with UAT stop IDs)
 * @param existingRoutes - Existing routes in Atlas
 * @param uatStopsMap - Optional map of UAT stop ID to UAT stop data (for coordinate-based fallback matching)
 * @returns Array of import results
 */
export function importUATRoutes(
  uatRoutes: Array<{
    route: UATRoute;
    routeId: string;
    routeName: string;
    routeColor: string;
    routeShape: [number, number][];
    stops: Array<{
      uatStopId: string;
      order: number;
      timeToNextStop: number;
      timeSpentAtStop: number;
    }>;
  }>,
  existingStops: FixedRouteStop[],
  existingRoutes: FixedRoute[],
  uatStopsMap?: Map<string, UATStop>,
  timezone?: string
): RouteImportResult[] {
  const results: RouteImportResult[] = [];
  
  // Counter for generating unique route IDs
  let routeIdCounter = 0;

  // Build lookup map: UAT stop ID -> Atlas stop
  const stopByUatId = new Map<string, FixedRouteStop>();
  // Also build coordinate-based lookup as fallback
  const stopByCoords = new Map<string, FixedRouteStop[]>();
  
  for (const stop of existingStops) {
    if (stop.uatStopId) {
      stopByUatId.set(stop.uatStopId, stop);
    }
    
    // Index by coordinates for fallback lookup
    const coordKey = `${Math.round(stop.coordinates.lat * 1000) / 1000},${Math.round(stop.coordinates.lng * 1000) / 1000}`;
    if (!stopByCoords.has(coordKey)) {
      stopByCoords.set(coordKey, []);
    }
    stopByCoords.get(coordKey)!.push(stop);
  }
  
  // Log only if debugging is needed
  // console.log('[importUATRoutes] Stop lookup map:', {
  //   totalStops: existingStops.length,
  //   stopsWithUatId: existingStops.filter(s => s.uatStopId).length,
  //   lookupMapSize: stopByUatId.size
  // });

  // Build lookup map: route ID -> existing route (to check for duplicates)
  const existingRouteById = new Map<string, FixedRoute>();
  for (const route of existingRoutes) {
    // We don't have UAT route IDs stored, so we'll create new routes
    // In the future, we could add uatRouteId to FixedRoute if needed
  }

  // Process each UAT route
  for (const uatRoute of uatRoutes) {
    const missingStops: string[] = [];
    const stopConfigs: FixedRouteStopConfig[] = [];
    const segments: FixedRouteSegment[] = [];
    const importedRouteName = (uatRoute.routeName || '').trim();
    const { baseName: routeName, versionSuffix } = splitRouteVersionSuffix(importedRouteName);

    // Map UAT stops to Atlas stops
    for (const uatStop of uatRoute.stops) {
      let atlasStop = stopByUatId.get(uatStop.uatStopId);

      // If not found by UAT ID, try to find by coordinates (fallback)
      // This handles cases where stops were matched by coordinates but don't have uatStopId yet
      if (!atlasStop && uatStopsMap) {
        const uatStopData = uatStopsMap.get(uatStop.uatStopId);
        if (uatStopData?.coordinates) {
          // Try coordinate-based matching
          const coordKey = `${Math.round(uatStopData.coordinates.lat * 1000) / 1000},${Math.round(uatStopData.coordinates.lng * 1000) / 1000}`;
          const candidates = stopByCoords.get(coordKey) || [];
          
          // Find closest match within tolerance
          for (const candidate of candidates) {
            const distance = calculateDistance(
              uatStopData.coordinates.lat,
              uatStopData.coordinates.lng,
              candidate.coordinates.lat,
              candidate.coordinates.lng
            );
            
            if (distance <= COORDINATE_TOLERANCE_METERS) {
              atlasStop = candidate;
              // Update the stop with uatStopId if it doesn't have one
              if (!atlasStop.uatStopId) {
                atlasStop = { ...atlasStop, uatStopId: uatStop.uatStopId };
              }
              // Log only if debugging is needed
              // console.log(`[importUATRoutes] Found stop ${uatStop.uatStopId} by coordinates: ${candidate.id}`);
              break;
            }
          }
        }
      }
      
      if (!atlasStop) {
        console.warn(`[importUATRoutes] Stop ${uatStop.uatStopId} not found by UAT ID or coordinates`);
        missingStops.push(uatStop.uatStopId);
        continue;
      }

      // Add stop config
      stopConfigs.push({
        stopId: atlasStop.id,
        waitTimeMinutes: uatStop.timeSpentAtStop,
      });

      // Add segment (if not the last stop)
      if (uatRoute.stops.length > 1 && uatStop.order < uatRoute.stops.length - 1) {
        const nextUatStop = uatRoute.stops[uatStop.order + 1];
        const nextAtlasStop = stopByUatId.get(nextUatStop.uatStopId);
        if (nextAtlasStop) {
          segments.push({
            fromStopId: atlasStop.id,
            toStopId: nextAtlasStop.id,
            travelTimeMinutes: uatStop.timeToNextStop,
          });
        }
      }
    }

    // Skip route if missing required stops
    if (missingStops.length > 0 || stopConfigs.length < 2) {
      // Even for skipped routes, calculate stopsHash if we have geometry
      const skippedRouteStops: FixedRouteStop[] = [];
      for (const config of stopConfigs) {
        const stop = Array.from(stopByUatId.values()).find(s => s.id === config.stopId);
        if (stop) {
          skippedRouteStops.push(stop);
        }
      }
      const skippedStopsHash = skippedRouteStops.length > 0 ? generateStopsHash(skippedRouteStops) : '';
      
      results.push({
        route: {
          id: `route-${Date.now()}-${++routeIdCounter}`,
          name: routeName,
          shortName: versionSuffix,
          startTime: '08:00', // Default, not available from UAT
          stops: stopConfigs,
          segments,
          color: uatRoute.routeColor,
          geometry: uatRoute.routeShape.length > 0 ? {
            coordinates: uatRoute.routeShape,
            calculatedAt: Date.now(),
            stopsHash: skippedStopsHash, // Calculate hash even for skipped routes
          } : undefined,
        },
        action: 'skipped',
        reason: missingStops.length > 0
          ? `Missing ${missingStops.length} stop(s)`
          : 'Less than 2 stops',
        missingStops,
      });
      continue;
    }

    // Try to extract start time from route data
    // Check if routeStops.next has expectedArrivalTime for first stop
    let startTime = '08:00'; // Default
    if (uatRoute.route.routeStops?.next && uatRoute.route.routeStops.next.length > 0) {
      const firstStop = uatRoute.route.routeStops.next[0];
      if (firstStop.expectedArrivalTime) {
        const parsedTime = parseTimeFromString(firstStop.expectedArrivalTime, timezone);
        if (parsedTime) {
          startTime = `${String(parsedTime.hours).padStart(2, '0')}:${String(parsedTime.minutes).padStart(2, '0')}`;
          // Log only if debugging is needed
          // console.log(`[importUATRoutes] Extracted start time from first stop: ${startTime}${timezone ? ` (in ${timezone})` : ''}`);
        }
      }
    } else if (uatRoute.route.stops && uatRoute.route.stops.length > 0) {
      // Try to get timing from route.stops if available
      // Check if there's timing data in the first stop
      const firstStop = uatRoute.route.stops[0];
      // If stops have timing info, we might need to infer start time differently
      // Log only if debugging is needed
      // console.log('[importUATRoutes] Using default start time, route.stops timing not available');
    }
    
    // Get route stops in order for hash calculation
    // Map stopConfigs to actual stops using the stopByUatId map
    const routeStopsInOrder: FixedRouteStop[] = [];
    for (const config of stopConfigs) {
      // Find the stop by matching the stopId from config
      const stop = Array.from(stopByUatId.values()).find(s => s.id === config.stopId);
      if (stop) {
        routeStopsInOrder.push(stop);
      }
    }
    
    // Calculate stopsHash for the route stops so the geometry from UAT is recognized as valid
    // This prevents recalculating geometry using OSRM/HERE when we already have it from UAT
    const stopsHash = routeStopsInOrder.length > 0 ? generateStopsHash(routeStopsInOrder) : '';
    
    // Validate and filter routeShape coordinates
    let validRouteShape: [number, number][] = [];
    if (uatRoute.routeShape && uatRoute.routeShape.length > 0) {
      validRouteShape = uatRoute.routeShape.filter((coord: any) => {
        if (!Array.isArray(coord) || coord.length < 2) return false;
        const lat = coord[0];
        const lng = coord[1];
        return typeof lat === 'number' && typeof lng === 'number' && 
               !isNaN(lat) && !isNaN(lng) && 
               isFinite(lat) && isFinite(lng);
      }) as [number, number][];
    }
    
    // Create new route with all metadata preserved
    const newRoute: FixedRoute = {
      id: `route-${Date.now()}-${++routeIdCounter}`,
      name: routeName,
      shortName: versionSuffix,
      startTime: startTime,
      stops: stopConfigs,
      segments,
      color: uatRoute.routeColor,
      geometry: validRouteShape.length >= 2 ? {
        coordinates: validRouteShape,
        calculatedAt: Date.now(),
        stopsHash: stopsHash, // Calculate hash so UAT geometry is recognized as valid
      } : undefined, // Don't set geometry if we don't have at least 2 valid coordinates
      // Preserve UAT metadata (will be enhanced by caller with environment/productId)
      uatRouteId: uatRoute.routeId,
      label: uatRoute.route.label || importedRouteName,
      uatMetadata: {
        originalId: uatRoute.routeId,
        originalName: importedRouteName,
        originalLabel: uatRoute.route.label,
        importedAt: Date.now(),
      },
    };
    

    results.push({
      route: newRoute,
      action: 'created',
      missingStops: [],
    });
  }

  return results;
}

/**
 * Summary of import operation
 */
export interface ImportSummary {
  stops: {
    total: number;
    created: number;
    updated: number;
    skipped: number;
  };
  routes: {
    total: number;
    created: number;
    skipped: number;
    totalMissingStops: number;
  };
}

/**
 * Generate import summary from results
 */
export function generateImportSummary(
  stopResults: StopImportResult[],
  routeResults: RouteImportResult[]
): ImportSummary {
  const stopSummary = {
    total: stopResults.length,
    created: stopResults.filter(r => r.action === 'created').length,
    updated: stopResults.filter(r => r.action === 'updated').length,
    skipped: stopResults.filter(r => r.action === 'skipped').length,
  };

  const routeSummary = {
    total: routeResults.length,
    created: routeResults.filter(r => r.action === 'created').length,
    skipped: routeResults.filter(r => r.action === 'skipped').length,
    totalMissingStops: routeResults.reduce((sum, r) => sum + r.missingStops.length, 0),
  };

  return {
    stops: stopSummary,
    routes: routeSummary,
  };
}

/**
 * Extract service settings (geofences and hubs) from UAT API response
 * 
 * @param response - UAT service settings response
 * @returns Object with geofences and hubs arrays
 */
export function extractUATServiceSettings(
  response: UATServiceSettingsResponse
): {
  geofences: UATGeofence[];
  hubs: UATHub[];
} {
  const result = {
    geofences: [] as UATGeofence[],
    hubs: [] as UATHub[],
  };

  if (!response || !response.data) {
    console.warn('[extractUATServiceSettings] No data in response');
    return result;
  }

  // Primary structure: data.data.serviceSettings[].geofences[] and .hubs[]
  if (response.data.data?.serviceSettings && Array.isArray(response.data.data.serviceSettings)) {
    for (const setting of response.data.data.serviceSettings) {
      // Collect geofences from this service setting
      if (setting.geofences && Array.isArray(setting.geofences)) {
        for (const gf of setting.geofences) {
          // Normalize geofence structure - handle the actual API format
          const geofence: UATGeofence = {
            id: gf.id || gf.geofenceId || uuid4(),
            name: gf.name || `Geofence ${gf.id || gf.geofenceId || 'Unknown'}`,
            type: gf.geofenceType || gf.type || 'inclusion',
            geometry: {
              type: 'Polygon', // API returns polygons with points array
              coordinates: gf.points ? gf.points.map((p: any) => [p.latitude, p.longitude]) : [],
            },
            ...gf, // Preserve all original fields (points, holes, zoneDetails, etc.)
          };
          result.geofences.push(geofence);
        }
      }
      
      // Collect hubs from this service setting
      if (setting.hubs && Array.isArray(setting.hubs)) {
        for (const hub of setting.hubs) {
          // Normalize hub structure - validate required fields
          const hubAny = hub as any; // Cast to any to access potentially missing properties
          const normalizedHub: UATHub = {
            addressId: hub.addressId || hubAny.id || '',
            icon: hub.icon || 'circle',
            name: hub.name || hub.label || '',
            label: hub.label || hub.name || '',
            placeId: hub.placeId || '',
            latitude: typeof hub.latitude === 'number' ? hub.latitude : 0,
            longitude: typeof hub.longitude === 'number' ? hub.longitude : 0,
            snapRadius: typeof hub.snapRadius === 'number' ? hub.snapRadius : 20,
            type: hub.type || 'poi',
          };
          result.hubs.push(normalizedHub);
        }
      }
    }
  }

  // Fallback: check for direct geofences/hubs (if API structure differs)
  if (result.geofences.length === 0) {
    const directGeofences = response.data.geofences || response.data.data?.geofences || [];
    if (Array.isArray(directGeofences)) {
      result.geofences = directGeofences.map((gf: any) => ({
        id: gf.id || gf.geofenceId || uuid4(),
        name: gf.name || `Geofence ${gf.id || 'Unknown'}`,
        type: gf.type || 'geofence',
        geometry: gf.geometry || {
          type: 'Polygon',
          coordinates: gf.points ? gf.points.map((p: any) => [p.latitude, p.longitude]) : [],
        },
        ...gf,
      }));
    }
  }

  if (result.hubs.length === 0) {
    const directHubs = response.data.hubs || response.data.data?.hubs || [];
    if (Array.isArray(directHubs)) {
      result.hubs = directHubs.map((hub: any) => ({
        addressId: hub.addressId || hub.id || '',
        icon: hub.icon || 'circle',
        name: hub.name || hub.label || '',
        label: hub.label || hub.name || '',
        placeId: hub.placeId || '',
        latitude: typeof hub.latitude === 'number' ? hub.latitude : 0,
        longitude: typeof hub.longitude === 'number' ? hub.longitude : 0,
        snapRadius: typeof hub.snapRadius === 'number' ? hub.snapRadius : 20,
        type: hub.type || 'poi',
      }));
    }
  }

  console.log('[extractUATServiceSettings] Extracted', result.geofences.length, 'geofences and', result.hubs.length, 'hubs');
  return result;
}

/**
 * Extract geofences from UAT service settings response
 * 
 * @param response - UAT service settings response
 * @returns Array of geofence objects
 */
export function extractUATGeofences(
  response: UATServiceSettingsResponse
): UATGeofence[] {
  const serviceSettings = extractUATServiceSettings(response);
  return serviceSettings.geofences;
}

/**
 * Extract hubs from UAT service settings response
 * 
 * @param response - UAT service settings response
 * @returns Array of hub objects
 */
export function extractUATHubs(
  response: UATServiceSettingsResponse
): UATHub[] {
  const serviceSettings = extractUATServiceSettings(response);
  return serviceSettings.hubs;
}

/**
 * Extract On-Demand (ODB) service information from UAT service settings response
 * 
 * ODB service information is identified by having serviceType field (e.g., 'doorToDoor')
 * and ODB-specific fields like serviceFlags, rideLockTimeOffset, maxCompanions
 * 
 * @param response - UAT service settings response
 * @returns Array of ODB service information objects
 */
export function extractUATODBServiceInformation(
  response: UATServiceSettingsResponse
): UATODBServiceInformation[] {
  const result: UATODBServiceInformation[] = [];

  if (!response || !response.data) {
    console.warn('[extractUATODBServiceInformation] No data in response');
    return result;
  }

  // Primary structure: data.data.serviceSettings[]
  let serviceSettings: any[] = [];
  
  if (response.data.data?.serviceSettings && Array.isArray(response.data.data.serviceSettings)) {
    serviceSettings = response.data.data.serviceSettings;
  } else if (response.data.serviceSettings && Array.isArray(response.data.serviceSettings)) {
    // Fallback: direct serviceSettings
    serviceSettings = response.data.serviceSettings;
  }

  for (const setting of serviceSettings) {
    // Identify ODB service information by presence of serviceType and ODB-specific fields
    // ODB services have: serviceType, serviceFlags, rideLockTimeOffset, maxCompanions
    // FR services typically don't have these fields (or have different structure)
    if (setting.serviceType && (setting.serviceFlags || setting.rideLockTimeOffset !== undefined || setting.maxCompanions !== undefined)) {
      // Extract and normalize capacities
      let capacities: UATCapacity[] = [];
      if (setting.capacities && Array.isArray(setting.capacities)) {
        capacities = setting.capacities.map((cap: any) => ({
          id: cap.id || uuid4(),
          onceOffTime: typeof cap.onceOffTime === 'number' ? cap.onceOffTime : 0,
          timePerUnit: typeof cap.timePerUnit === 'number' ? cap.timePerUnit : 0,
          movable: typeof cap.movable === 'boolean' ? cap.movable : false,
          isDependant: typeof cap.isDependant === 'boolean' ? cap.isDependant : false,
          type: {
            name: cap.type?.name || 'standard',
            iconName: cap.type?.iconName || null,
            displayOrder: typeof cap.type?.displayOrder === 'number' ? cap.type.displayOrder : 0,
          },
        }));
      }

      const odbServiceInfo: UATODBServiceInformation = {
        id: setting.id || uuid4(),
        name: setting.name || 'Unnamed ODB Service',
        description: setting.description || '',
        startDate: setting.startDate || '',
        endDate: setting.endDate || null,
        bookingStartDate: setting.bookingStartDate,
        serviceType: setting.serviceType,
        serviceFlags: setting.serviceFlags || {
          hubsEnabled: false,
          geofencesEnabled: false,
          breaksEnabled: false,
          zonesEnabled: false,
          isHubToHub: false,
        },
        timezone: setting.timezone || 'UTC',
        rideLockTimeOffset: setting.rideLockTimeOffset || 0,
        maxCompanions: setting.maxCompanions || 0,
        networkId: setting.networkId || '',
        capacities: capacities.length > 0 ? capacities : undefined,
        solutions: setting.solutions || [],
        ...setting, // Preserve all original fields
      };
      result.push(odbServiceInfo);
    }
  }

  console.log('[extractUATODBServiceInformation] Extracted', result.length, 'ODB service information entries');
  return result;
}

/**
 * Import result for ODB service information
 */
export interface ODBServiceInformationImportResult {
  serviceInfoId: string;
  action: 'created' | 'skipped' | 'updated';
  reason?: string;
}

/**
 * Extract service settings from UAT API response and map to internal Service format
 * 
 * This function extracts all service settings (both ODB and Fixed Route) from the UAT response
 * and maps them to the internal Service interface format used in the ServiceMenu drawer.
 * 
 * @param response - UAT service settings response
 * @param environment - Environment from which the service was downloaded (UAT/Production)
 * @param productId - Product ID used for download
 * @param serviceDate - Service date used for download (YYYY-MM-DD)
 * @returns Array of Service objects ready for use in ServiceMenu
 */
export function extractUATServices(
  response: UATServiceSettingsResponse,
  environment: string = 'UAT',
  productId: string = '',
  serviceDate: string = ''
): Service[] {
  const result: Service[] = [];

  if (!response || !response.data) {
    console.warn('[extractUATServices] No data in response');
    return result;
  }

  // Primary structure: data.data.serviceSettings[]
  let serviceSettings: any[] = [];
  
  if (response.data.data?.serviceSettings && Array.isArray(response.data.data.serviceSettings)) {
    serviceSettings = response.data.data.serviceSettings;
  } else if (response.data.serviceSettings && Array.isArray(response.data.serviceSettings)) {
    // Fallback: direct serviceSettings
    serviceSettings = response.data.serviceSettings;
  }

  for (const setting of serviceSettings) {
    // Extract and normalize capacities
    const capacities: Capacity[] = [];
    if (setting.capacities && Array.isArray(setting.capacities)) {
      setting.capacities.forEach((cap: any) => {
        capacities.push({
          id: cap.id || uuid4(),
          onceOffTime: typeof cap.onceOffTime === 'number' ? cap.onceOffTime : 0,
          timePerUnit: typeof cap.timePerUnit === 'number' ? cap.timePerUnit : 0,
          movable: typeof cap.movable === 'boolean' ? cap.movable : false,
          isDependant: typeof cap.isDependant === 'boolean' ? cap.isDependant : false,
          type: {
            name: cap.type?.name || 'standard',
            iconName: cap.type?.iconName || null,
            displayOrder: typeof cap.type?.displayOrder === 'number' ? cap.type.displayOrder : 0,
          },
        });
      });
    }

    // Extract processing times from capacities (if not explicitly provided)
    const processingTimes: ProcessingTime[] = [];
    if (capacities.length > 0) {
      capacities.forEach(cap => {
        processingTimes.push({
          seatType: cap.type.name,
          processingTimeSeconds: cap.onceOffTime,
        });
      });
    } else {
      // Default processing times if no capacities
      processingTimes.push(
        { seatType: 'standard', processingTimeSeconds: 30 },
        { seatType: 'wheelchair', processingTimeSeconds: 90 }
      );
    }

    // Extract time window solutions
    const solutions: TimeWindowSolution[] = [];
    if (setting.solutions && Array.isArray(setting.solutions)) {
      setting.solutions.forEach((sol: any) => {
        if (sol.start && sol.end) {
          solutions.push({
            start: sol.start,
            end: sol.end,
          });
        }
      });
    }

    // Extract service flags
    const serviceFlags: ServiceFlags = {
      hubsEnabled: setting.serviceFlags?.hubsEnabled || false,
      geofencesEnabled: setting.serviceFlags?.geofencesEnabled || false,
      breaksEnabled: setting.serviceFlags?.breaksEnabled || false,
      zonesEnabled: setting.serviceFlags?.zonesEnabled || false,
      isHubToHub: setting.serviceFlags?.isHubToHub || false,
    };

    // Create Service object
    const service: Service = {
      id: `service-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: setting.name || 'Unnamed Service',
      description: setting.description || '',
      serviceType: setting.serviceType || 'doorToDoor',
      serviceFlags,
      timezone: setting.timezone || 'UTC',
      rideLockTimeOffset: setting.rideLockTimeOffset || 45,
      maxCompanions: setting.maxCompanions || 6,
      networkId: setting.networkId || '',
      capacities: capacities.length > 0 ? capacities : defaultCapacities,
      solutions: solutions.length > 0 ? solutions : undefined,
      geofences: setting.geofences || undefined,
      hubs: setting.hubs || undefined,
      timeWindowSettings: {
        slope: 1.7, // Default, will be editable
        intercept: 600,
        fixedWindow: 900,
      },
      maxTripTimeMinutes: 60, // Default 1 hour
      processingTimes,
      onceOffProcessingTime: capacities.length > 0 ? capacities[0]?.onceOffTime || 15 : 15,
      travelTimeMultiplier: 1.1, // Default
      useVariableMultipliers: false,
      hourlyMultipliers: undefined,
      uatMetadata: {
        importedAt: Date.now(),
        importedFrom: environment,
        productId,
        serviceDate,
        originalId: setting.id,
        originalName: setting.name,
        originalData: setting, // Preserve all original fields
      },
    };

    result.push(service);
  }

  console.log('[extractUATServices] Extracted', result.length, 'service settings');
  return result;
}

/**
 * Import ODB service information from UAT into Atlas
 * 
 * @param serviceInfo - Array of ODB service information objects from UAT
 * @param existingServiceInfo - Existing service information in Atlas (for deduplication)
 * @returns Array of import results
 */
export function importUATODBServiceInformation(
  serviceInfo: UATODBServiceInformation[],
  existingServiceInfo: any[] = []
): ODBServiceInformationImportResult[] {
  const results: ODBServiceInformationImportResult[] = [];
  
  if (!serviceInfo || serviceInfo.length === 0) {
    console.warn('[importUATODBServiceInformation] No service information to import');
    return results;
  }
  
  // TODO: Implement actual import logic based on Atlas's service information storage
  // For now, this is a placeholder that returns results
  for (const info of serviceInfo) {
    const serviceInfoId = info.id;
    
    // Check if already exists
    const existing = existingServiceInfo.find((si: any) => si.id === serviceInfoId || si.uatServiceInfoId === serviceInfoId);
    
    if (existing) {
      results.push({
        serviceInfoId,
        action: 'skipped',
        reason: 'Service information already exists',
      });
    } else {
      // TODO: Create service information in Atlas
      // This will need to be implemented based on how service information is stored
      results.push({
        serviceInfoId,
        action: 'created',
      });
    }
  }
  
  console.log('[importUATODBServiceInformation] Imported', results.filter(r => r.action === 'created').length, 'service information entries');
  return results;
}

/**
 * Convert a circle (center + radius) to a polygon approximation
 * Creates a polygon with 32 points to approximate a circle
 * 
 * @param center - Center point {latitude, longitude}
 * @param radius - Radius in meters
 * @returns Array of coordinates forming a circular polygon
 */
function circleToPolygon(
  center: { latitude: number; longitude: number },
  radius: number
): [number, number][] {
  const points: [number, number][] = [];
  const numPoints = 32; // Number of points to approximate the circle
  
  // Convert radius from meters to degrees (approximate)
  // 1 degree latitude ≈ 111,000 meters
  // 1 degree longitude ≈ 111,000 * cos(latitude) meters
  const latRadius = radius / 111000;
  const lngRadius = radius / (111000 * Math.cos(center.latitude * Math.PI / 180));
  
  for (let i = 0; i < numPoints; i++) {
    const angle = (2 * Math.PI * i) / numPoints;
    const lat = center.latitude + latRadius * Math.cos(angle);
    const lng = center.longitude + lngRadius * Math.sin(angle);
    points.push([lat, lng]);
  }
  
  // Close the polygon by adding the first point at the end
  points.push(points[0]);
  
  return points;
}

/**
 * Import result for a single geofence
 */
export interface GeofenceImportResult {
  geofenceId: string;
  action: 'created' | 'skipped';
  reason?: string;
}

/**
 * Import geofences from UAT into Atlas as map layers
 * 
 * @param geofences - Array of geofence objects from UAT
 * @param context - AppContext for map layer management
 * @param layerName - Optional custom layer name (default: "UAT Geofences - {date}")
 * @returns Array of import results
 */
export function importUATGeofences(
  geofences: UATGeofence[],
  context: Context,
  layerName?: string
): GeofenceImportResult[] {
  const results: GeofenceImportResult[] = [];
  
  if (!geofences || geofences.length === 0) {
    console.warn('[importUATGeofences] No geofences to import');
    return results;
  }
  
  // Create a new layer for imported geofences
  const defaultLayerName = layerName || `UAT Geofences - ${new Date().toLocaleDateString()}`;
  const newLayerId = context.global.mapLayers.length;
  
  // Create new layer
  const newLayer = {
    layerId: newLayerId,
    layerName: defaultLayerName,
    shapes: [] as any[],
    hidden: false,
  };
  
  // Import each geofence as a polygon
  for (const geofence of geofences) {
    const geofenceId = geofence.id || geofence.name || `geofence-${Date.now()}-${Math.random()}`;
    
    try {
      let coordinates: [number, number][] | null = null;
      
      // Handle different geometry types
      if (geofence.geometry) {
        const geom = geofence.geometry;
        
        if (geom.type === 'Polygon' || geom.type === 'polygon') {
          // Polygon coordinates - handle different formats
          if (Array.isArray(geom.coordinates)) {
            // Check if it's already in the right format [[lat, lng], [lat, lng], ...]
            if (Array.isArray(geom.coordinates[0]) && typeof geom.coordinates[0][0] === 'number') {
              coordinates = geom.coordinates as [number, number][];
            } else if (Array.isArray(geom.coordinates[0]) && Array.isArray(geom.coordinates[0][0])) {
              // Nested array format - take first ring
              const ring = geom.coordinates[0];
              if (Array.isArray(ring) && ring.length > 0) {
                coordinates = ring.map((coord: any) => {
                  // Handle [lng, lat] vs [lat, lng] formats
                  if (Array.isArray(coord) && coord.length >= 2) {
                    // GeoJSON uses [lng, lat], but we use [lat, lng]
                    return [coord[1] || coord[0], coord[0] || coord[1]] as [number, number];
                  }
                  return [0, 0] as [number, number];
                });
              }
            }
          }
        } else if (geom.type === 'Circle' || geom.type === 'circle') {
          // Circle - convert to polygon
          if (geom.center && typeof geom.center.latitude === 'number' && typeof geom.center.longitude === 'number') {
            const radius = geom.radius || 100; // Default 100 meters if not specified
            coordinates = circleToPolygon(geom.center, radius);
          }
        }
      }
      
      // If no coordinates found, try to extract from geofence directly
      if (!coordinates) {
        const geofenceAny = geofence as any;
        
        // Check for points array (API format: points: [{latitude, longitude}, ...])
        if (geofenceAny.points && Array.isArray(geofenceAny.points)) {
          coordinates = geofenceAny.points.map((p: any) => {
            if (p.latitude !== undefined && p.longitude !== undefined) {
              return [p.latitude, p.longitude] as [number, number];
            }
            return null;
          }).filter((coord: [number, number] | null): coord is [number, number] => coord !== null);
        } else if (geofenceAny.coordinates && Array.isArray(geofenceAny.coordinates)) {
          coordinates = geofenceAny.coordinates as [number, number][];
        } else if (geofenceAny.polygon && Array.isArray(geofenceAny.polygon)) {
          coordinates = geofenceAny.polygon as [number, number][];
        } else if (geofenceAny.latitude && geofenceAny.longitude && geofenceAny.radius) {
          // Circle format with direct properties
          coordinates = circleToPolygon(
            { latitude: geofenceAny.latitude, longitude: geofenceAny.longitude },
            geofenceAny.radius
          );
        }
      }
      
      // Validate coordinates
      if (!coordinates || coordinates.length < 3) {
        console.warn(`[importUATGeofences] Geofence ${geofenceId} has invalid or missing coordinates`, geofence);
        results.push({
          geofenceId,
          action: 'skipped',
          reason: 'Invalid or missing coordinates',
        });
        continue;
      }
      
      // Validate coordinate values
      const validCoordinates = coordinates.filter((coord) => {
        return Array.isArray(coord) &&
               coord.length >= 2 &&
               typeof coord[0] === 'number' &&
               typeof coord[1] === 'number' &&
               !isNaN(coord[0]) &&
               !isNaN(coord[1]) &&
               isFinite(coord[0]) &&
               isFinite(coord[1]);
      });
      
      if (validCoordinates.length < 3) {
        console.warn(`[importUATGeofences] Geofence ${geofenceId} has insufficient valid coordinates`, geofence);
        results.push({
          geofenceId,
          action: 'skipped',
          reason: 'Insufficient valid coordinates',
        });
        continue;
      }
      
      // Create polygon shape
      const polygon = {
        id: `geofence-${geofenceId}-${Date.now()}`,
        layerId: newLayerId,
        type: 'polygon' as const,
        name: geofence.name || `Geofence ${geofenceId}`,
        description: geofence.type || '',
        colour: '#ff6b6b', // Default red color for geofences
        opacity: '0.3',
        borderColour: '#ff6b6b',
        borderWidth: 2,
        isLocked: false,
        coordinates: [validCoordinates], // Polygon format expects array of arrays
        createdAt: Date.now(),
        // Store UAT metadata
        uatMetadata: {
          originalId: geofenceId,
          originalName: geofence.name,
          originalType: geofence.type,
          importedAt: Date.now(),
        },
      };
      
      newLayer.shapes.push(polygon);
      results.push({
        geofenceId,
        action: 'created',
      });
      
      console.log(`[importUATGeofences] Imported geofence ${geofenceId}:`, {
        name: geofence.name,
        type: geofence.type,
        coordinatesCount: validCoordinates.length,
      });
    } catch (error) {
      console.error(`[importUATGeofences] Error importing geofence ${geofenceId}:`, error);
      results.push({
        geofenceId,
        action: 'skipped',
        reason: `Error: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }
  
  // Add the new layer to the map
  if (newLayer.shapes.length > 0) {
    context.global.setLayers((prevLayers) => [...prevLayers, newLayer]);
    context.global.setIdCounter((prev) => prev + 1);
    
    console.log(`[importUATGeofences] Created layer "${defaultLayerName}" with ${newLayer.shapes.length} geofences`);
    
    // Auto-center map on imported geofences
    try {
      const allLatLngs: [number, number][] = [];
      
      // Collect all coordinates from imported geofences
      newLayer.shapes.forEach((shape: any) => {
        if (shape.type === 'polygon' && shape.coordinates && Array.isArray(shape.coordinates)) {
          // For polygons, extract all coordinates from all rings
          shape.coordinates.forEach((ring: [number, number][]) => {
            if (Array.isArray(ring)) {
              ring.forEach((coord: [number, number]) => {
                if (Array.isArray(coord) && coord.length >= 2 &&
                    typeof coord[0] === 'number' && typeof coord[1] === 'number' &&
                    !isNaN(coord[0]) && !isNaN(coord[1]) &&
                    coord[0] !== 0 && coord[1] !== 0) {
                  allLatLngs.push(coord);
                }
              });
            }
          });
        }
      });
      
      if (allLatLngs.length > 0) {
        // Create point shapes for the zoom event
        const shapes: Array<{ type: 'point'; coordinates: [number, number]; hidden?: boolean }> = [];
        allLatLngs.forEach(coord => {
          shapes.push({
            type: 'point',
            coordinates: coord,
            hidden: false
          });
        });
        
        // Dispatch zoom event to center map on imported geofences
        const event = new CustomEvent('zoomToLayer', {
          detail: { shapes }
        });
        window.dispatchEvent(event);
        
        console.log(`[importUATGeofences] Centered map on ${allLatLngs.length} coordinates from imported geofences`);
      }
    } catch (error) {
      console.warn('[importUATGeofences] Failed to center map on imported geofences:', error);
      // Don't fail the import if centering fails
    }
  } else {
    console.warn('[importUATGeofences] No valid geofences to import, layer not created');
  }
  
  return results;
}

/**
 * Import result for a single meeting point or POI
 */
export interface HubImportResult {
  hubId: string;
  action: 'created' | 'skipped';
  reason?: string;
  layerId?: number;
  pointId?: string;
}

/**
 * Import meeting points from UAT hubs into Atlas as map points
 * 
 * @param hubs - Array of UAT hub objects to import as meeting points
 * @param context - AppContext for map layer management
 * @param layerName - Optional custom layer name (default: "UAT Meeting Points - {date}")
 * @returns Array of import results
 */
export function importUATMeetingPoints(
  hubs: UATHub[],
  context: Context,
  layerName?: string
): HubImportResult[] {
  const results: HubImportResult[] = [];
  
  if (!hubs || hubs.length === 0) {
    console.warn('[importUATMeetingPoints] No hubs to import');
    return results;
  }
  
  // Create a new layer for imported meeting points
  const defaultLayerName = layerName || `UAT Meeting Points - ${new Date().toLocaleDateString()}`;
  const { global } = context;
  const { mapLayers, setLayers, idCounter, setIdCounter, setActiveLayerId } = global;
  
  // Find existing layer or create new one
  let meetingPointsLayer = mapLayers.find(l => l.layerName === defaultLayerName);
  
  if (!meetingPointsLayer) {
    const newLayerId = idCounter;
    setIdCounter(idCounter + 1);
    
    meetingPointsLayer = {
      layerId: newLayerId,
      layerName: defaultLayerName,
      shapes: [],
      hidden: false,
      expanded: true,
    };
    setLayers((prev: Layer[]) => [...prev, meetingPointsLayer!]);
    setActiveLayerId(newLayerId);
    console.log(`[importUATMeetingPoints] Created new layer: "${defaultLayerName}" (ID: ${newLayerId})`);
  } else {
    setActiveLayerId(meetingPointsLayer.layerId);
    console.log(`[importUATMeetingPoints] Reusing existing layer: "${defaultLayerName}" (ID: ${meetingPointsLayer.layerId})`);
  }
  
  const importedPoints: Point[] = [];
  
  // Import each hub as a point
  for (const hub of hubs) {
    const hubId = hub.addressId || uuid4();
    
    try {
      // Validate coordinates
      if (typeof hub.latitude !== 'number' || typeof hub.longitude !== 'number' ||
          isNaN(hub.latitude) || isNaN(hub.longitude) ||
          !isFinite(hub.latitude) || !isFinite(hub.longitude)) {
        console.warn(`[importUATMeetingPoints] Hub ${hubId} has invalid coordinates`, hub);
        results.push({
          hubId,
          action: 'skipped',
          reason: 'Invalid or missing coordinates',
        });
        continue;
      }
      
      /**
       * Hub-to-Point Conversion:
       * 
       * Converts a UAT hub object to an Atlas Point shape for map display.
       * 
       * Hub Structure (from API):
       * - addressId: UUID identifier
       * - name: Hub name
       * - label: Full address label
       * - latitude/longitude: Coordinates
       * - placeId: Google Places ID
       * - snapRadius: Snap radius in meters
       * - type: Type identifier (e.g., "poi" for POIs, other values for meeting points)
       * - icon: Icon type (e.g., "circle")
       * 
       * Point Structure (for map):
       * - id: Generated unique identifier
       * - name: Hub name
       * - description: Hub label (address) + metadata
       * - coordinates: [latitude, longitude] array
       * - colour: Visual color (green for meeting points, orange for POIs)
       * - icon: Icon type for map display
       * - size: Point size (based on snapRadius)
       * - uatMetadata: Original hub data preserved for reference
       */
      const point: Point = {
        id: `meeting-point-${hubId}-${Date.now()}`,
        layerId: meetingPointsLayer.layerId,
        type: 'point',
        name: hub.name || `Meeting Point ${hubId.substring(0, 8)}`,
        description: hub.label || hub.name || '',
        colour: '#4caf50', // Green color for meeting points
        icon: 'location',
        size: 24,
        isLocked: false,
        coordinates: [hub.latitude, hub.longitude],
        hidden: false,
        // Store UAT metadata in a custom field (we'll add this to Point type if needed, or use description)
        // For now, we'll store key metadata in description
        // Note: We could extend Point type to include uatMetadata similar to Polygon
      };
      
      // Store metadata in description (append to existing description)
      const metadata = {
        addressId: hub.addressId,
        placeId: hub.placeId,
        snapRadius: hub.snapRadius,
        type: hub.type,
        icon: hub.icon,
      };
      point.description = point.description 
        ? `${point.description} | Metadata: ${JSON.stringify(metadata)}`
        : `Metadata: ${JSON.stringify(metadata)}`;
      
      importedPoints.push(point);
      results.push({
        hubId,
        action: 'created',
        layerId: meetingPointsLayer.layerId,
        pointId: point.id,
      });
      
      console.log(`[importUATMeetingPoints] Imported meeting point ${hubId}:`, {
        name: hub.name,
        coordinates: [hub.latitude, hub.longitude],
      });
    } catch (error) {
      console.error(`[importUATMeetingPoints] Error importing hub ${hubId}:`, error);
      results.push({
        hubId,
        action: 'skipped',
        reason: `Error: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }
  
  // Add all points to the layer
  if (importedPoints.length > 0) {
    const updatedLayer: Layer = {
      ...meetingPointsLayer,
      shapes: [...meetingPointsLayer.shapes, ...importedPoints],
    };
    setLayers((prev: Layer[]) => prev.map(l => (l.layerId === updatedLayer.layerId ? updatedLayer : l)));
    console.log(`[importUATMeetingPoints] Added ${importedPoints.length} meeting points to layer "${updatedLayer.layerName}"`);
  }
  
  // Auto-center map on imported meeting points
  try {
    const allLatLngs: [number, number][] = importedPoints.map(p => p.coordinates);
    
    if (allLatLngs.length > 0) {
      const shapes: Array<{ type: 'point'; coordinates: [number, number]; hidden?: boolean }> = [];
      allLatLngs.forEach(coord => {
        shapes.push({
          type: 'point',
          coordinates: coord,
          hidden: false
        });
      });
      
      const event = new CustomEvent('zoomToLayer', {
        detail: { shapes }
      });
      window.dispatchEvent(event);
      
      console.log(`[importUATMeetingPoints] Centered map on ${allLatLngs.length} meeting points`);
    }
  } catch (error) {
    console.warn('[importUATMeetingPoints] Failed to center map on imported meeting points:', error);
  }
  
  return results;
}

/**
 * Import points of interest (POIs) from UAT hubs into Atlas as map points
 * 
 * @param hubs - Array of UAT hub objects to import as POIs
 * @param context - AppContext for map layer management
 * @param layerName - Optional custom layer name (default: "UAT Points of Interest - {date}")
 * @returns Array of import results
 */
export function importUATPointsOfInterest(
  hubs: UATHub[],
  context: Context,
  layerName?: string
): HubImportResult[] {
  const results: HubImportResult[] = [];
  
  if (!hubs || hubs.length === 0) {
    console.warn('[importUATPointsOfInterest] No hubs to import');
    return results;
  }
  
  // Create a new layer for imported POIs
  const defaultLayerName = layerName || `UAT Points of Interest - ${new Date().toLocaleDateString()}`;
  const { global } = context;
  const { mapLayers, setLayers, idCounter, setIdCounter, setActiveLayerId } = global;
  
  // Find existing layer or create new one
  let poisLayer = mapLayers.find(l => l.layerName === defaultLayerName);
  
  if (!poisLayer) {
    const newLayerId = idCounter;
    setIdCounter(idCounter + 1);
    
    poisLayer = {
      layerId: newLayerId,
      layerName: defaultLayerName,
      shapes: [],
      hidden: false,
      expanded: true,
    };
    setLayers((prev: Layer[]) => [...prev, poisLayer!]);
    setActiveLayerId(newLayerId);
    console.log(`[importUATPointsOfInterest] Created new layer: "${defaultLayerName}" (ID: ${newLayerId})`);
  } else {
    setActiveLayerId(poisLayer.layerId);
    console.log(`[importUATPointsOfInterest] Reusing existing layer: "${defaultLayerName}" (ID: ${poisLayer.layerId})`);
  }
  
  const importedPoints: Point[] = [];
  
  // Import each hub as a POI point
  for (const hub of hubs) {
    const hubId = hub.addressId || uuid4();
    
    try {
      // Validate coordinates
      if (typeof hub.latitude !== 'number' || typeof hub.longitude !== 'number' ||
          isNaN(hub.latitude) || isNaN(hub.longitude) ||
          !isFinite(hub.latitude) || !isFinite(hub.longitude)) {
        console.warn(`[importUATPointsOfInterest] Hub ${hubId} has invalid coordinates`, hub);
        results.push({
          hubId,
          action: 'skipped',
          reason: 'Invalid or missing coordinates',
        });
        continue;
      }
      
      /**
       * Hub-to-Point Conversion:
       * 
       * Converts a UAT hub object to an Atlas Point shape for map display.
       * Same conversion logic as meeting points, but with POI-specific styling.
       * 
       * Hub Structure (from API):
       * - addressId: UUID identifier
       * - name: Hub name
       * - label: Full address label
       * - latitude/longitude: Coordinates
       * - placeId: Google Places ID
       * - snapRadius: Snap radius in meters
       * - type: Type identifier (must be "poi" for POIs)
       * - icon: Icon type (e.g., "circle")
       * 
       * Point Structure (for map):
       * - id: Generated unique identifier
       * - name: Hub name
       * - description: Hub label (address) + metadata
       * - coordinates: [latitude, longitude] array
       * - colour: Visual color (orange for POIs)
       * - icon: Icon type for map display (place icon)
       * - size: Point size (based on snapRadius)
       * - uatMetadata: Original hub data preserved for reference
       */
      const point: Point = {
        id: `poi-${hubId}-${Date.now()}`,
        layerId: poisLayer.layerId,
        type: 'point',
        name: hub.name || `POI ${hubId.substring(0, 8)}`,
        description: hub.label || hub.name || '',
        colour: '#ff9800', // Orange color for POIs
        icon: 'location',
        size: 24,
        isLocked: false,
        coordinates: [hub.latitude, hub.longitude],
        hidden: false,
      };
      
      // Store metadata in description
      const metadata = {
        addressId: hub.addressId,
        placeId: hub.placeId,
        snapRadius: hub.snapRadius,
        type: hub.type,
        icon: hub.icon,
      };
      point.description = point.description 
        ? `${point.description} | Metadata: ${JSON.stringify(metadata)}`
        : `Metadata: ${JSON.stringify(metadata)}`;
      
      importedPoints.push(point);
      results.push({
        hubId,
        action: 'created',
        layerId: poisLayer.layerId,
        pointId: point.id,
      });
      
      console.log(`[importUATPointsOfInterest] Imported POI ${hubId}:`, {
        name: hub.name,
        coordinates: [hub.latitude, hub.longitude],
      });
    } catch (error) {
      console.error(`[importUATPointsOfInterest] Error importing hub ${hubId}:`, error);
      results.push({
        hubId,
        action: 'skipped',
        reason: `Error: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }
  
  // Add all points to the layer
  if (importedPoints.length > 0) {
    const updatedLayer: Layer = {
      ...poisLayer,
      shapes: [...poisLayer.shapes, ...importedPoints],
    };
    setLayers((prev: Layer[]) => prev.map(l => (l.layerId === updatedLayer.layerId ? updatedLayer : l)));
    console.log(`[importUATPointsOfInterest] Added ${importedPoints.length} POIs to layer "${updatedLayer.layerName}"`);
  }
  
  // Auto-center map on imported POIs
  try {
    const allLatLngs: [number, number][] = importedPoints.map(p => p.coordinates);
    
    if (allLatLngs.length > 0) {
      const shapes: Array<{ type: 'point'; coordinates: [number, number]; hidden?: boolean }> = [];
      allLatLngs.forEach(coord => {
        shapes.push({
          type: 'point',
          coordinates: coord,
          hidden: false
        });
      });
      
      const event = new CustomEvent('zoomToLayer', {
        detail: { shapes }
      });
      window.dispatchEvent(event);
      
      console.log(`[importUATPointsOfInterest] Centered map on ${allLatLngs.length} POIs`);
    }
  } catch (error) {
    console.warn('[importUATPointsOfInterest] Failed to center map on imported POIs:', error);
  }
  
  return results;
}

/**
 * Import hubs from UAT (legacy function - use importUATMeetingPoints or importUATPointsOfInterest instead)
 * This function is kept for backward compatibility but delegates to importUATMeetingPoints
 * 
 * @param hubs - Array of hub objects from UAT
 * @param context - AppContext for map layer management
 * @returns Array of import results
 */
export function importUATHubs(
  hubs: UATHub[],
  context: Context
): HubImportResult[] {
  // Delegate to meeting points import for backward compatibility
  console.log('[importUATHubs] Delegating to importUATMeetingPoints (use importUATMeetingPoints or importUATPointsOfInterest directly)');
  return importUATMeetingPoints(hubs, context, `UAT Hubs - ${new Date().toLocaleDateString()}`);
}

/**
 * Extract coordinates and addresses from ride segments
 * 
 * @param segments - Array of ride segments (pickup/dropoff)
 * @returns Object with pickup and dropoff coordinates and addresses, or null if missing
 */
function extractCoordinatesFromSegments(segments: UATRideSegment[] | undefined): {
  from_latitude: number;
  from_longitude: number;
  to_latitude: number;
  to_longitude: number;
  from_address?: string;
  to_address?: string;
} | null {
  if (!segments || !Array.isArray(segments) || segments.length === 0) {
    return null;
  }

  const pickupSegment = segments.find(s => s.activity === 'pickup');
  const dropoffSegment = segments.find(s => s.activity === 'dropoff');

  if (!pickupSegment || !pickupSegment.address?.position) {
    console.warn('[extractCoordinatesFromSegments] Missing pickup segment or address position');
    return null;
  }

  if (!dropoffSegment || !dropoffSegment.address?.position) {
    console.warn('[extractCoordinatesFromSegments] Missing dropoff segment or address position');
    return null;
  }

  return {
    from_latitude: pickupSegment.address.position.latitude,
    from_longitude: pickupSegment.address.position.longitude,
    to_latitude: dropoffSegment.address.position.latitude,
    to_longitude: dropoffSegment.address.position.longitude,
    from_address: pickupSegment.address.label,
    to_address: dropoffSegment.address.label,
  };
}

/**
 * Map trip passengers to PassengerType array
 * Combines seats of the same type into single entries with counts
 * 
 * @param tripPassengers - Array of seat objects from UAT
 * @returns Array of PassengerType objects
 */
function mapTripPassengersToPassengerTypes(tripPassengers: any[] | undefined): PassengerType[] {
  if (!tripPassengers || !Array.isArray(tripPassengers) || tripPassengers.length === 0) {
    // Default to 1 standard passenger if no passengers specified
    return [{ number_of_passengers: 1, type: 'standard' }];
  }

  // Count passengers by type
  const passengerCounts = new Map<string, number>();
  
  for (const seat of tripPassengers) {
    const seatType = seat.seatType || 'standard';
    const currentCount = passengerCounts.get(seatType) || 0;
    passengerCounts.set(seatType, currentCount + 1);
  }

  // Convert to PassengerType array
  const result: PassengerType[] = [];
  for (const [type, count] of Array.from(passengerCounts.entries())) {
    result.push({
      number_of_passengers: count,
      type: type,
    });
  }

  return result.length > 0 ? result : [{ number_of_passengers: 1, type: 'standard' }];
}

/**
 * Map UAT ride type to RideSchema request_type
 * 
 * @param uatType - UAT ride type (e.g., "ARRIVAL_FIXED", "DEPARTURE_FIXED")
 * @returns RideSchema request_type (e.g., "arrival_fixed", "departure_fixed")
 */
function mapUATRideTypeToRequestType(uatType: string | undefined): string | undefined {
  if (!uatType) return undefined;
  
  // Convert to lowercase with underscores (e.g., "ARRIVAL_FIXED" -> "arrival_fixed")
  return uatType.toLowerCase();
}

/**
 * Extract rides from UAT rides response and convert to RideSchema[]
 * 
 * This function handles rides that may or may not have segments in the list response.
 * If segments are missing, the ride will be skipped (caller should fetch detailed info first).
 * 
 * @param response - UAT rides response (from getridelist or detailed responses)
 * @returns Array of RideSchema objects (only rides with valid coordinates)
 */
export function extractUATRides(
  response: UATRidesResponse | { entries: UATRideEntry[] | UATRideDetailedEntry[] },
  timezone?: string
): RideSchema[] {
  const result: RideSchema[] = [];

  // Handle both UATRidesResponse and direct entries array
  let entries: (UATRideEntry | UATRideDetailedEntry)[] = [];
  if ('data' in response && response.data?.entries) {
    entries = response.data.entries;
  } else if ('entries' in response && Array.isArray(response.entries)) {
    entries = response.entries;
  } else {
    console.warn('[extractUATRides] No entries found in response');
    return result;
  }

  console.log(`[extractUATRides] Processing ${entries.length} ride entries`);

  for (const entry of entries) {
    try {
      // Extract coordinates from segments
      const coords = extractCoordinatesFromSegments(entry.segments);
      
      if (!coords) {
        console.warn(`[extractUATRides] Skipping ride ${entry.id} - missing segments/coordinates`);
        continue;
      }

      // Get user ID (prefer owner.id, fallback to user.id)
      const userId = entry.owner?.id || entry.user?.id;
      if (!userId) {
        console.warn(`[extractUATRides] Skipping ride ${entry.id} - missing user ID`);
        continue;
      }

      // Get request time (prefer departureTime, fallback to time)
      const rawRequestTime = entry.departureTime || entry.time;
      if (!rawRequestTime) {
        console.warn(`[extractUATRides] Skipping ride ${entry.id} - missing request time`);
        continue;
      }
      
      // Convert UTC time from API to network timezone, then strip timezone to make it timezone-agnostic
      // This ensures we preserve the local time (as shown in production) rather than UTC time
      let requestTime: string;
      if (timezone) {
        // Convert from UTC to network timezone, then strip timezone
        requestTime = convertUTCToNetworkTimezone(rawRequestTime, timezone);
      } else {
        // Fallback: just strip timezone (preserves whatever timezone was in the string)
        requestTime = stripTimezoneFromDateTime(rawRequestTime);
      }

      // Map passengers
      const passengers = mapTripPassengersToPassengerTypes(entry.tripPassengers);

      // Map request type - ensure it's properly preserved
      // Try multiple sources: entry.type, tripType, or derive from rideType
      let requestType: string | undefined = undefined;
      
      // First, try entry.type (e.g., "ARRIVAL_FIXED", "DEPARTURE_FIXED")
      if (entry.type) {
        requestType = mapUATRideTypeToRequestType(entry.type);
        console.log(`[extractUATRides] Ride ${entry.id} - Using entry.type "${entry.type}" -> request_type "${requestType}"`);
      }
      
      // If no type, try tripType (e.g., "leaveAt" -> "departure_fixed", "arriveBy" -> "arrival_fixed")
      if (!requestType && entry.tripType) {
        if (entry.tripType.toLowerCase() === 'leaveat') {
          requestType = 'departure_fixed';
        } else if (entry.tripType.toLowerCase() === 'arriveby') {
          requestType = 'arrival_fixed';
        }
        console.log(`[extractUATRides] Ride ${entry.id} - Using tripType "${entry.tripType}" -> request_type "${requestType}"`);
      }
      
      // If still no type, try rideType (e.g., "on-demand" -> "on_demand")
      if (!requestType && entry.rideType) {
        requestType = entry.rideType.toLowerCase().replace(/-/g, '_');
        console.log(`[extractUATRides] Ride ${entry.id} - Using rideType "${entry.rideType}" -> request_type "${requestType}"`);
      }
      
      // Log warning if we still don't have a type
      if (!requestType) {
        console.warn(`[extractUATRides] Ride ${entry.id} has no type information. Available fields:`, {
          type: entry.type,
          tripType: entry.tripType,
          rideType: entry.rideType,
          allKeys: Object.keys(entry),
        });
      }

      // Create RideSchema - preserve all important fields from UAT ride entry
      const rideSchema: RideSchema = {
        external_reference_id: entry.id, // Use UUID as external reference
        ride_id: entry.id,
        external_user_reference_id: userId,
        user_id: userId,
        from_latitude: coords.from_latitude,
        from_longitude: coords.from_longitude,
        to_latitude: coords.to_latitude,
        to_longitude: coords.to_longitude,
        from_address: coords.from_address,
        to_address: coords.to_address,
        request_type: requestType || undefined, // Preserve request type
        passengers: passengers,
        request_time: requestTime,
        created_at: entry.createdAt,
        batch_id: entry.mergedId || undefined,
        // Preserve priority if available (may be in entry or entry metadata)
        priority: (entry as any).priority || undefined,
      };

      // Store original entry data in a way that can be preserved through the import process
      // We'll attach it to the RideSchema object as a non-enumerable property
      Object.defineProperty(rideSchema, '_originalEntry', {
        value: entry,
        enumerable: false,
        writable: false,
        configurable: false,
      });

      result.push(applyRideAddressPlaceholders(rideSchema, result.length + 1));
    } catch (error) {
      console.error(`[extractUATRides] Error processing ride ${entry.id}:`, error);
      // Continue processing other rides
    }
  }

  console.log(`[extractUATRides] Extracted ${result.length} valid rides from ${entries.length} entries`);
  return result;
}

/**
 * Import result for a single ride
 */
export interface RideImportResult {
  rideId: string;
  action: 'created' | 'skipped';
  reason?: string;
  lineId?: string;
  originPointId?: string;
  destPointId?: string;
  layerId?: number;
}

/**
 * Convert RideSchema to Line object for map display
 * 
 * @param ride - RideSchema object to convert
 * @param originPointId - ID of the origin point (pickup location)
 * @param destPointId - ID of the destination point (dropoff location)
 * @returns Line object ready for map display
 */
export function convertRideSchemaToLine(
  ride: RideSchema,
  originPointId: string,
  destPointId: string
): Line & { originPointId: string; destPointId: string; from_address?: string; to_address?: string; request_time?: string; uatMetadata?: any } {
  const rideWithAddresses = applyRideAddressPlaceholders(ride, 1);
  // Extract passenger counts
  let standardSeat = 0;
  let wheelchair = 0;
  let numPassengers = 0;

  if (rideWithAddresses.passengers && Array.isArray(rideWithAddresses.passengers)) {
    for (const passenger of rideWithAddresses.passengers) {
      const count = passenger.number_of_passengers || 0;
      numPassengers += count;
      
      if (passenger.type === 'standard' || passenger.type === 'Standard') {
        standardSeat += count;
      } else if (passenger.type === 'wheelchair' || passenger.type === 'Wheelchair') {
        wheelchair += count;
      }
    }
  }

  // Default to 1 standard passenger if no passengers specified
  if (numPassengers === 0) {
    numPassengers = 1;
    standardSeat = 1;
  }

  // Format request time (preserve as-is - RideSchema.request_time is always string)
  const requestTimeString: string | undefined = rideWithAddresses.request_time;

  // Create Line object following the pattern from GenerateRidesDrawer
  const line: Line & { originPointId: string; destPointId: string; from_address?: string; to_address?: string; request_time?: string; uatMetadata?: any } = {
    id: rideWithAddresses.ride_id || rideWithAddresses.external_reference_id || uuid4(),
    type: 'line',
    name: rideWithAddresses.ride_id || rideWithAddresses.external_reference_id || 'Ride',
    ride_id: rideWithAddresses.ride_id || rideWithAddresses.external_reference_id,
    user_id: rideWithAddresses.user_id || rideWithAddresses.external_user_reference_id,
    colour: '#0000FF', // Blue color for rides (same as GenerateRidesDrawer)
    description: '',
    opacity: '1',
    borderColour: '#0000FF',
    borderWidth: 2,
    isLocked: false,
    coordinates: [
      [rideWithAddresses.from_latitude, rideWithAddresses.from_longitude],
      [rideWithAddresses.to_latitude, rideWithAddresses.to_longitude]
    ],
    arrowStyle: 'arrow',
    time: requestTimeString,
    numPassengers: numPassengers,
    standard_seat: standardSeat,
    wheelchair: wheelchair,
    request_type: rideWithAddresses.request_type || undefined, // Ensure request_type is set (or undefined, not empty string)
    originPointId: originPointId,
    destPointId: destPointId,
    from_address: rideWithAddresses.from_address,
    to_address: rideWithAddresses.to_address,
    request_time: requestTimeString,
    hidden: false,
  };

  // Store original UAT metadata for reference - preserve all important fields
  // This allows us to maintain request_type and other values from the ride pulls
  const originalEntry = (rideWithAddresses as any)._originalEntry;
  line.uatMetadata = {
    external_reference_id: rideWithAddresses.external_reference_id,
    external_user_reference_id: rideWithAddresses.external_user_reference_id,
    created_at: rideWithAddresses.created_at,
    batch_id: rideWithAddresses.batch_id,
    priority: rideWithAddresses.priority,
    from_point_id: rideWithAddresses.from_point_id,
    to_point_id: rideWithAddresses.to_point_id,
    request_type: rideWithAddresses.request_type, // Preserve request type in metadata
    request_time: rideWithAddresses.request_time, // Preserve request time in metadata
    // Preserve additional fields from original UAT ride entry
    ...(originalEntry ? {
      status: originalEntry.status,
      rideType: originalEntry.rideType,
      tripType: originalEntry.tripType,
      journey: originalEntry.journey,
      arrivalTime: originalEntry.arrivalTime,
      updatedAt: originalEntry.updatedAt,
      displayId: originalEntry.displayId,
      unassignedReason: originalEntry.unassignedReason,
      network: originalEntry.network,
      dbId: originalEntry.dbId,
      createdBy: originalEntry.createdBy,
      owner: originalEntry.owner,
      user: originalEntry.user,
      occupiedSeats: originalEntry.occupiedSeats,
      tripPassengers: originalEntry.tripPassengers,
      // Preserve any additional fields that might be present
      originalType: originalEntry.type, // Preserve original type before mapping
    } : {}),
  };

  return line;
}

/**
 * Import rides from UAT into Atlas as Lines and Points
 * 
 * Creates origin and destination Points for each ride, then creates Lines connecting them.
 * All shapes are added to a new or existing map layer.
 * 
 * @param rides - Array of RideSchema objects to import
 * @param context - AppContext for map layer management
 * @param layerName - Optional custom layer name (default: "UAT Rides - {date}")
 * @returns Array of import results
 */
export function importUATRides(
  rides: RideSchema[],
  context: Context,
  layerName?: string
): RideImportResult[] {
  const results: RideImportResult[] = [];

  if (!rides || rides.length === 0) {
    console.warn('[importUATRides] No rides to import');
    return results;
  }

  if (!context || !context.global) {
    throw new Error('[importUATRides] Context or global state is missing');
  }

  const global = context.global;

  // Generate layer name if not provided
  // Default format: "{Environment} Rides - {date}" (e.g., "UAT Rides - 12/5/2025" or "Prod Rides - 12/5/2025")
  const finalLayerName = layerName || `UAT Rides - ${new Date().toLocaleDateString()}`;

  // Find existing layer with same name or create new one
  let ridesLayer: Layer | null = null;
  const existingLayerIndex = global.mapLayers.findIndex((l: Layer) => l.layerName === finalLayerName);
  
  if (existingLayerIndex >= 0) {
    ridesLayer = global.mapLayers[existingLayerIndex];
    console.log(`[importUATRides] Using existing layer: ${finalLayerName} (ID: ${ridesLayer.layerId})`);
  } else {
    // Create new layer
    const existingLayerIds = global.mapLayers
      .map((l: Layer) => l.layerId)
      .filter((id): id is number => id !== undefined && id !== null);
    
    const newLayerId = existingLayerIds.length > 0
      ? Math.max(...existingLayerIds) + 1
      : global.mapLayers.length;
    
    ridesLayer = {
      layerId: newLayerId,
      layerName: finalLayerName,
      hidden: false,
      shapes: [],
    };
    
    console.log(`[importUATRides] Creating new layer: ${finalLayerName} (ID: ${newLayerId})`);
  }

  const importedPoints: Point[] = [];
  const importedLines: (Line & { originPointId: string; destPointId: string })[] = [];
  const pointIdMap = new Map<string, { originId: string; destId: string }>(); // Map ride ID to point IDs

  // Process each ride
  for (const ride of rides) {
    const rideId = ride.ride_id || ride.external_reference_id || uuid4();
    
    try {
      // Validate coordinates
      if (typeof ride.from_latitude !== 'number' || typeof ride.from_longitude !== 'number' ||
          typeof ride.to_latitude !== 'number' || typeof ride.to_longitude !== 'number' ||
          isNaN(ride.from_latitude) || isNaN(ride.from_longitude) ||
          isNaN(ride.to_latitude) || isNaN(ride.to_longitude) ||
          !isFinite(ride.from_latitude) || !isFinite(ride.from_longitude) ||
          !isFinite(ride.to_latitude) || !isFinite(ride.to_longitude)) {
        console.warn(`[importUATRides] Ride ${rideId} has invalid coordinates`, ride);
        results.push({
          rideId,
          action: 'skipped',
          reason: 'Invalid or missing coordinates',
        });
        continue;
      }

      // Create origin point (pickup location)
      const originPointId = `ride-origin-${rideId}-${Date.now()}`;
      const originPoint: Point = {
        id: originPointId,
        layerId: ridesLayer.layerId,
        type: 'point',
        name: ride.from_address || `Pickup ${rideId.substring(0, 8)}`,
        description: ride.from_address || '',
        colour: '#FF0000', // Red for pickup
        icon: 'location',
        size: 24,
        isLocked: false,
        coordinates: [ride.from_latitude, ride.from_longitude],
        hidden: false,
      };

      // Create destination point (dropoff location)
      const destPointId = `ride-dest-${rideId}-${Date.now()}`;
      const destPoint: Point = {
        id: destPointId,
        layerId: ridesLayer.layerId,
        type: 'point',
        name: ride.to_address || `Dropoff ${rideId.substring(0, 8)}`,
        description: ride.to_address || '',
        colour: '#00FF00', // Green for dropoff
        icon: 'location',
        size: 24,
        isLocked: false,
        coordinates: [ride.to_latitude, ride.to_longitude],
        hidden: false,
      };

      importedPoints.push(originPoint, destPoint);
      pointIdMap.set(rideId, { originId: originPointId, destId: destPointId });

      // Create line from ride
      const line = convertRideSchemaToLine(ride, originPointId, destPointId);
      line.layerId = ridesLayer.layerId;
      importedLines.push(line);

      results.push({
        rideId,
        action: 'created',
        lineId: line.id,
        originPointId: originPointId,
        destPointId: destPointId,
        layerId: ridesLayer.layerId,
      });

      console.log(`[importUATRides] Imported ride ${rideId}:`, {
        from: [ride.from_latitude, ride.from_longitude],
        to: [ride.to_latitude, ride.to_longitude],
      });
    } catch (error) {
      console.error(`[importUATRides] Error importing ride ${rideId}:`, error);
      results.push({
        rideId,
        action: 'skipped',
        reason: `Error: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }

  // Add all points and lines to the layer
  if (importedPoints.length > 0 || importedLines.length > 0) {
    const updatedLayer: Layer = {
      ...ridesLayer,
      shapes: [...ridesLayer.shapes, ...importedPoints, ...importedLines],
    };

    // Update or add layer
    if (existingLayerIndex >= 0) {
      // Update existing layer
      const updatedLayers = [...global.mapLayers];
      updatedLayers[existingLayerIndex] = updatedLayer;
      global.setLayers(updatedLayers);
    } else {
      // Add new layer
      global.setLayers((prev: Layer[]) => [...prev, updatedLayer]);
      global.setActiveLayerId(updatedLayer.layerId!);
    }

    console.log(`[importUATRides] Added ${importedPoints.length} points and ${importedLines.length} lines to layer ${finalLayerName}`);
  }

  // Auto-center map on imported rides
  try {
    const allLatLngs: [number, number][] = [];
    
    // Collect coordinates from imported points (pickup and dropoff locations)
    importedPoints.forEach(point => {
      if (point.coordinates && 
          Array.isArray(point.coordinates) && 
          point.coordinates.length >= 2 &&
          typeof point.coordinates[0] === 'number' && 
          typeof point.coordinates[1] === 'number' &&
          !isNaN(point.coordinates[0]) && 
          !isNaN(point.coordinates[1]) &&
          point.coordinates[0] !== 0 && 
          point.coordinates[1] !== 0) {
        allLatLngs.push([point.coordinates[0], point.coordinates[1]]);
      }
    });
    
    // Also collect coordinates from lines (ride paths)
    importedLines.forEach(line => {
      if (line.coordinates && Array.isArray(line.coordinates)) {
        line.coordinates.forEach((coord: any) => {
          if (Array.isArray(coord) && coord.length >= 2) {
            const lat = coord[0];
            const lng = coord[1];
            if (typeof lat === 'number' && typeof lng === 'number' && 
                !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              allLatLngs.push([lat, lng]);
            }
          }
        });
      }
    });
    
    if (allLatLngs.length > 0) {
      // Create point shapes for the zoom event
      const shapes: Array<{ type: 'point'; coordinates: [number, number]; hidden?: boolean }> = [];
      
      // Add all coordinates as point shapes
      allLatLngs.forEach(coord => {
        shapes.push({
          type: 'point',
          coordinates: coord,
          hidden: false
        });
      });
      
      // Dispatch zoom event to center map on imported rides
      const event = new CustomEvent('zoomToLayer', {
        detail: { shapes }
      });
      window.dispatchEvent(event);
      
      console.log(`[importUATRides] Centered map on ${allLatLngs.length} coordinates from imported rides`);
    }
  } catch (error) {
    console.warn('[importUATRides] Failed to center map on imported rides:', error);
  }

  return results;
}
